(ns ductile.ui.components
  (:require ["@popperjs/core" :as popper]
            ["@radix-ui/react-icons" :as Icons]
            ["@radix-ui/react-tabs" :as Tabs]
            ["@radix-ui/react-toast" :as Toast]
            ["@radix-ui/react-tooltip" :as Tooltip]
            ["html5sortable/dist/html5sortable.cjs" :as Sortable]
            ["luxon" :refer [DateTime]]
            ["react" :refer [createContext]]
            ["react-dom" :as react-dom]
            ["react-imask" :as imask]
            ["scroll-into-view-if-needed" :as scrollIntoView]
            [applied-science.js-interop :as j]
            [camel-snake-kebab.core :as camel-snake-kebab]
            [clojure.edn :as edn]
            [clojure.string :as str]
            [ductile.base64u :as base64u]
            [ductile.clerk.render]
            [ductile.clerk.viewer]
            [ductile.schema :as schema]
            [ductile.service.label :as service.label]
            [ductile.time.format :as time-format]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.http :as http]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.router :as router]
            [ductile.ui.scene :as scene]
            [ductile.ui.scope]
            [ductile.ui.util :as util]
            [ductile.uis :as uis]
            [goog.functions :as gf]
            [goog.labs.userAgent.platform :as platform]
            [kitchen-async.promise :as p]
            [nextjournal.clerk.render :as clerk.render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.render.localstorage :as localstorage]
            [nextjournal.clerk.viewer :as viewer]
            [nextjournal.log :as log]
            [nexus.core :as nexus]
            [re-frame.core :as re-frame]
            [reagent.core :as r])
  (:require-macros [ductile.ui.macros :refer [slurp-resource defc]]
                   [ductile.ui.scene.macros :refer [defscene]]
                   [ductile.ui.scope.macros :refer [with-scope-protected-value when-current-user-scope]]))


(declare button icon spinner slot button-opts->classes input select)

;; re-exports from ductile.uis
(def class-vec uis/class-vec)
(def opts+content uis/opts+content)
(def vin uis/vin)
(def link uis/link)

(def z-index
  "high to low"
  (-> [:env-bar
       :tooltip
       :context-menu
       :toast
       :combobox-choices
       :modal-close
       :alert-content
       :dialog-content
       :alert-backdrop
       :modal-backdrop
       :sticky-header
       :sticky-subheader]
      reverse
      (zipmap (map (partial + 1000) (range)))))

(def nexus
  {:nexus/system->state deref
   :nexus/actions
   {:store/assoc
    (fn [_state k v]
      [[:store/tx [[:op/assoc k v]]]])
    :store/assoc-in
    (fn [_state path v]
      [[:store/tx [[:op/assoc-in path v]]]])
    :store/dissoc
    (fn [_state & ks]
      [[:store/tx [(into [:op/dissoc] ks)]]])
    :store/dissoc-in
    (fn [_store path]
      [[:store/tx [[:op/dissoc-in path]]]])
    :store/route-assoc
    (fn [_state k v]
      [[:store/tx [[:op/assoc-in [:current-route/transient k] v]]]])
    :store/route-assoc-in
    (fn [_state path v]
      [[:store/tx [[:op/assoc-in (into [:current-route/transient] path) v]]]])
    :store/route-update
    (fn [_state k f & args]
      [[:store/tx [[:op/update-in [:current-route/transient k] f args]]]])
    :store/route-update-in
    (fn [_state path f & args]
      [[:store/tx [[:op/update-in (into [:current-route/transient] path) f args]]]])
    :store/route-dissoc
    (fn [_state k]
      [[:store/tx [[:op/dissoc-in [:current-route/transient k]]]]])
    :store/route-dissoc-in
    (fn [_state path]
      [[:store/tx [[:op/dissoc-in (into [:current-route/transient] path)]]]])}
   :nexus/effects
   {:store/tx
    (fn [_ store txs]
      (swap! store deframe/process-tx txs))
    :effects/prevent-default
    (fn [{:keys [dispatch-data]} _]
      (some-> dispatch-data :dom-event .preventDefault))
    :effects/set-path
    (fn [_ _ route-template params]
      (when-some [new-path (router/path route-template params)]
        (router/navigate-to new-path)))}
   :nexus/placeholders
   {:event.target/value (fn [dispatch-data]
                          (some-> dispatch-data :dom-event .-target .-value))}})

(defn build-handler [effect-specs]
  (fn [event]
    (nexus/dispatch nexus (:app-db re-frame/default-frame) {:dom-event event} effect-specs)))

(defn with-events [opts]
  (if-let [on-events (:on opts)]
    (-> opts
        (dissoc :on)
        (merge
         (into {}
               (map (fn [[event-type effect-specs]]
                      [(keyword (str "on-" (name event-type)))
                       (build-handler effect-specs)])
                    on-events))))
    opts))

(defn use-localstorage
  ([localstorage-key] (use-localstorage localstorage-key nil))
  ([localstorage-key defaults] (use-localstorage localstorage-key defaults nil))
  ([localstorage-key defaults reset-when]
   (let [saved (try (localstorage/get-item (str localstorage-key))
                    (catch :default e
                      (js/console.error "Failed to restore use-localstorage" (str localstorage-key) e)
                      nil))
         initial (cond (and defaults
                            (or (nil? saved)
                                (not= (empty saved) (empty defaults))
                                (and reset-when (reset-when saved)))) defaults
                       (and (map? defaults) (map? saved)) (merge defaults saved)
                       :else saved)
         !state (hooks/use-state initial)]
     (hooks/use-effect
      (fn []
        (localstorage/set-item! (str localstorage-key) (pr-str @!state))
        nil)
      [@!state])
     !state)))

(def is-mac?
  (platform/isMacintosh))

(def is-mobile?
  (or (platform/isAndroid) (platform/isIphone)))

(defn use-mobile?
  ([] (use-mobile? 960))
  ([threshold]
   (let [!width (hooks/use-state (.-innerWidth js/window))
         update-width #(reset! !width (.-innerWidth js/window))]
     (hooks/use-effect
      (fn []
        (.addEventListener js/window "resize" update-width)
        #(.removeEventListener js/window "resize" update-width))
      [])
     (< @!width threshold))))

(def modifier-keys
  {:cmd (if is-mac? "⌘" "ctrl")
   :option (if is-mac? "⌥" "alt")
   :shift "⇧"})

(defn platform-key [k]
  (modifier-keys k (if (keyword? k) (name k) k)))


(def local-icons
  (dissoc
   (->> (ns-publics 'ductile.ui.icons)
        (map (fn [[k v]] [(keyword k) @v]))
        (remove (comp fn? second))
        (into {}))
   :logo :event-colors :letter))

(defn maybe-add-host [src]
  (cond->> src
    (and (exists? js/location) (str/starts-with? js/location.host  "localhost:"))
    (str "https://dev.ductile.de:8888/")))

(def academy-base-urls
  {"dev.ductile.de:8888" "https://academy.dev.ductile.de:8000"})

(defn academy-base-url []
  (let [host (when (exists? js/location)
               js/location.host)]
    (cond
      (str/starts-with? host "localhost:") "https://academy.dev.ductile.de:8000"
      :else (or (get academy-base-urls host)
                "https://academy.ductile.de"))))

(defn academy-url [path]
  (str (academy-base-url)
       (if (str/starts-with? path "/")
         path
         (str "/" path))))

(defn icon-socket
  ([icon] [icon-socket {} icon])
  ([opts icon]
   [:div.rounded-full.flex.items-center.justify-center.flex-shrink-0.text-white
    {:class ["w-[24px]" "h-[24px]" (str "bg-" (name (or (:variant opts) :sky)) "-500")]}
    icon]))

(def all-icons (set
                (->> Icons js-keys js->clj (map #(str/replace % #"Icon$" "")) (map camel-snake-kebab/->kebab-case-keyword))))

(defscene icons
  "This is a reference of all available icons. Use them with `[icon :name]`."
  :params (r/atom {:icons all-icons
                   :show-all? false
                   :search ""})
  [!state]
  (let [{:keys [icons show-all? search]} @!state
        featured [:x :check :chevron-down :chevron-right
                  :square :check-square :arrow-right :menu
                  :lock :circle :check-circle :x-circle :car
                  :truck :ship :anchor :train :parking :scan
                  :map-pin :email :trash :trash-2 :x-octagon
                  :check-circled :calendar :map :edit-3
                  :link-2 :globe :pencil2 :update :backpack
                  :archive :refresh-ccw :more-horizontal
                  :alert-triangle :help-circle :plus
                  :shopping-bag :damage :align-justify
                  :lock :unlock :download :minimize-2 :maximize-2]
        ref-fn (fn [el]
                 (when (and el (not (icons "help-circle")))
                   (swap! !state update :icons conj (map #(.getAttribute % "id")
                                                         (.querySelectorAll el "symbol")))))]
    [:<>
     [:div.pb-2
      [input {:value search
              :size 4
              :placeholder "Search icons"
              :on-change (fn [e]
                           (js/console.log "change" e (.. e -target -value))
                           (swap! !state assoc :search (.. e -target -value)))}]]
     [:div.hidden
      {:ref ref-fn
       :dangerouslySetInnerHTML
       {:__html (slurp-resource "../resources/public/images/feather-sprite.svg")}}]
     (if-let [icons (->> icons
                         (filter (fn [i] (str/includes? (str/lower-case (str i)) (str/lower-case search))))
                         (map keyword)
                         (filter identity)
                         seq)]
       [:div.bg-black.p-6
        (into [:div.flex.flex-wrap]
              (map (fn [icon-name]
                     [:div.text-white.bg-white.bg-opacity-25.mr-4.mb-4.rounded.relative.flex.items-center.justify-center.cursor-pointer.hover:ring-2.ring-white.ring-offset-2
                      {:style {:width 100 :height 100}
                       :on-click #(.writeText js/navigator.clipboard (pr-str icon-name))}
                      [icon {:size 30} icon-name]
                      [:div.pb-1.text-center.px-1.absolute.bottom-0.left-0.right-0
                       {:class "text-[10px]"}
                       (name icon-name)]]))
              (cond (seq search) icons
                    show-all? icons
                    :else featured))
        [button {:on-click #(do (swap! !state update :show-all? not)
                                (swap! !state assoc :search ""))
                 :style {:width "100%"}}
         (if show-all? "Show less icons" "Show all icons")]]
       "No results")]))

(defn ->radix-name [icon-name]
  (when icon-name
    (str (str/join "" (map str/capitalize (str/split (name icon-name) #"-"))) "Icon")))

(def icon-alias {:x :cross-2})

(def prefer-feather-icons #{:camera :arrow-left :arrow-right :arrow-up :arrow-down})

(defn icon
  ([icon-name] [icon {} icon-name])
  ([options icon-name]
   (let [size (:size options)
         options (if size
                   (merge (dissoc options :size)
                          {:width size :height size})
                   options)
         comp (and (not (contains? prefer-feather-icons icon-name))
                   (j/get Icons (->radix-name icon-name) (j/get Icons (->radix-name (icon-alias icon-name)))))]
     (if comp
       [:> comp options]
       (let [size (or size 24)]
         (if-let [local-icon (and (not (contains? prefer-feather-icons icon-name))
                                  (get local-icons icon-name))]
           (update local-icon 1 merge {:width size :height size})
           [:svg (merge {:width size
                         :height size
                         :fill "none"
                         :stroke "currentColor"
                         :stroke-width 2
                         :stroke-linecap "round"
                         :stroke-linejoin "round"}
                        options)
            [:use {"xlinkHref" (maybe-add-host (str (cond->> "images/feather-sprite.svg#"
                                                      (not= "snapshots.ductile.de" js/location.host)
                                                      (str "/"))
                                                    (name icon-name)))}]]))))))

(def invoice-event-icon
  [:div.rounded-full.flex.items-center.justify-center
   {:class "w-[28px] h-[28px] bg-indigo-600 text-white"}
   [icon {:width 16 :height 16} :file-text]])

(def cancelled-icon
  [:div.rounded-full.flex.items-center.justify-center
   {:class "w-[28px] h-[28px] bg-mauve8 text-white"}
   [icon {:width 16 :height 16} :cross-2]])

(def undone-icon
  [:div.rounded-full.flex.items-center.justify-center
   {:class "w-[28px] h-[28px] bg-mauve8 text-white"}
   [icon {:width 16 :height 16} :reset]])

(def edit-icon
  [:div.rounded-full.flex.items-center.justify-center
   {:class "w-[28px] h-[28px] bg-mauve8 text-white"}
   [icon {:width 16 :height 16} :edit-3]])

(def park-icon
  [:div.rounded-full.flex.items-center.justify-center
   {:class "w-[28px] h-[28px]" :style {:color "white" :background-color (:in-storage icons/event-colors)}}
   [icon :car]])

(def history-context (createContext false))

(defonce !field-state (r/atom {:labels->fields {}
                               :ordered-fields []}))

#_(add-watch !field-state :debug-field-state (fn [_ _ _ state]
                                               (js/console.log "--- FIELD STATES HAVE CHANGED ---")
                                               (js/console.log state)))

(defn use-field-state [label state]
  #_(js/console.log "use-field-state" label state)
  (hooks/use-effect (fn []
                      (swap! !field-state (fn [field-state]
                                            (-> field-state
                                                (assoc-in [:labels->fields label] state)
                                                (update :ordered-fields conj label))))
                      (fn []
                        (swap! !field-state (fn [field-state]
                                              (-> field-state
                                                  (update :labels->fields dissoc label)
                                                  (update :ordered-fields (fn [fields]
                                                                            (->> fields (remove #(= % label)) vec)))))))))
  (r/cursor !field-state [:labels->fields label]))

(defn next-val [value v]
  (let [i (.indexOf v value)]
    (nth v (if (= i (dec (count v))) 0 (inc i)))))

(defn prev-val [value v]
  (let [i (.indexOf v value)]
    (nth v (if (= i 0) (dec (count v)) (dec i)))))

(defn focus-field [{:as state :keys [focused]} label]
  (cond-> state
    focused (assoc-in [:labels->fields focused :focused?] false)
    true (assoc :focused label)
    true (assoc-in [:labels->fields label :focused?] true)))

(defn focus-field! [selection-fn]
  (swap! !field-state (fn [{:as state :keys [ordered-fields focused]}]
                        (focus-field state (if focused
                                             (selection-fn focused ordered-fields)
                                             (first ordered-fields))))))


(defn quit-editing-field! []
  (swap! !field-state (fn [{:as state :keys [editing]}]
                        (if editing
                          (-> state
                              (assoc-in [:labels->fields editing :editing?] false)
                              (dissoc :editing))
                          state))))

(defn quit+focus-next-field! [label event]
  (when-not (.. event -target (closest "input"))
    (quit-editing-field!)
    (focus-field! next-val)))

(defn edit-field! [label]
  (swap! !field-state (fn [{:as state :keys [editing]}]
                        (cond-> state
                          editing (assoc-in [:labels->fields editing :editing?] false)
                          true (assoc-in [:labels->fields label :editing?] true)
                          true (focus-field label)
                          true (assoc :editing label)))))

(defn toggle-editing-field! [label]
  (if (get-in @!field-state [:labels->fields label :editing?])
    (quit-editing-field!)
    (edit-field! label)))

(defn use-focusable-fields []
  (hooks/use-effect (fn []
                      (let [handler (fn [event]
                                      (let [{:keys [editing focused]} @!field-state]
                                        (when (not editing)
                                          (cond
                                            (contains? #{"ArrowDown" "ArrowRight"} (.-key event))
                                            (do (.preventDefault event) (focus-field! next-val))
                                            (contains? #{"ArrowUp" "ArrowLeft"} (.-key event))
                                            (focus-field! prev-val)
                                            (= (.-key event) "Enter")
                                            (when focused (edit-field! focused))))))]
                        (js/addEventListener "keydown" handler)
                        #(js/removeEventListener "keydown" handler)))))

(defn use-key-down-events [key-map]
  (hooks/use-effect (fn []
                      (let [handler (fn [event]
                                      (doseq [[key-name key-handler] key-map]
                                        (when (or (and (set? key-name) (contains? key-name (.-key event)))
                                                  (= key-name (.-key event)))
                                          (key-handler event))))]
                        (js/addEventListener "keydown" handler)
                        #(js/removeEventListener "keydown" handler)))
                    key-map))

(defn key-down-handler [key-map]
  (use-key-down-events key-map)
  [:<>])

(defn use-navigation-guard
  "Guards both in-app navigation and browser tab close/refresh.
   Pass a message string to activate, nil to deactivate."
  [msg]
  (hooks/use-effect
   (fn []
     (router/set-navigation-guard! msg)
     #(router/set-navigation-guard! nil))
   [msg]))

(defonce !debug-mode? (r/atom (localstorage/get-item "debug-mode")))

(defn debug-mode? []
  @!debug-mode?)

(defn toggle-debug-mode! []
  (swap! !debug-mode? not)
  (localstorage/set-item! "debug-mode" @!debug-mode?))

(defn debug [& xs]
  (when @!debug-mode?
    (into [:<>]
          (map (fn [x] [:pre.opacity-90 {:class "text-[10px]"}
                        #_(nextjournal.clerk.render/inspect x)
                        (pr-str x)]))
          xs)))

(defn debug+ [& xs]
  (when @!debug-mode?
    [:div.bg-white.px-3.py-1.text-slate-900.overflow-x-auto.shadow-inner.border.rounded.mt-3
     (apply debug xs)]))

(def makes-with-logos
  #{"Aixam" "Alfa Romeo" "Aston Martin" "Audi" "Bentley" "BMW" "Cadillac" "Chevrolet" "Chrysler" "Citroen" "Dacia" "Daewoo" "Daihatsu" "Daimler" "Dodge" "Ds" "Ferrari" "Fiat" "Fisker" "Ford" "Great Wall" "Honda" "Hummer" "Hyundai" "Infiniti" "Isuzu" "Iveco" "Jaecoo" "Jaguar" "Jeep" "Kia" "Lamborghini" "Lancia" "Land Rover" "Lexus" "Ligier" "Maserati" "Maxus" "Mazda" "Mercedes Benz" "Mini" "Mitsubishi" "Mobilvetta" "Nissan" "Opel" "OMODA" "Peugeot" "Piaggio" "Porsche" "Renault" "Rover" "Saab" "Seat" "Sixt" "Skoda" "Smart" "Ssangyong" "Subaru" "Suzuki" "Tata" "Tesla" "Toyota" "Volkswagen" "Volvo"})

(defn make-logo [make-name & attrs]
  [:img (merge {:style {:object-fit "contain" :object-position "center"}
                :src (if (and make-name (contains? makes-with-logos make-name))
                       (str "https://storage.googleapis.com/ductile-data/static-data/make-logos/"
                            (-> make-name
                                (str/lower-case)
                                (str/replace #" " "-"))
                            ".png")
                       "/images/default-make-logo.png")}
               attrs)])

(defscene make
  "Renders the logo of a make, takes a two-letter make code and an optional
  hiccup attributes map."
  (into [:div.flex.items-center.p-6.bg-black.flex-wrap]
        (map (fn [make-name]
               [:div.flex.flex-shrink-0.items-center.justify-center.rounded.mr-4.mb-4.p-4.bg-white
                {:style {:width 100 :height 100}}
                [make-logo make-name]])
             makes-with-logos)))

(defn loading-spinner []
  [:div.flex.items-center.text-indigo-600.absolute
   {:style {:left "50%" :top "50%" :transform "translate(-50%, -50%)"}}
   icons/spinner
   [:span.font-bold.text-2xl.ml-4.opacity-25 "Loading…"]])

(defn native-date-picker [label value on-value!]
  [:div.flex.items-center.gap-2
   (when label [:label label])
   [:input.tabular-nums
    {:type "date"
     :class (button-opts->classes {:variant :white})
     :value value
     :on-key-down (fn [e] (when (= (.-key e) "Enter") (on-value! (.. e
                                                                     -target
                                                                     -value))))
     :on-change (fn [e] (on-value! (.. e
                                       -target
                                       -value)))}]])

(defn native-datetime-picker [label value on-value!]
  [:div.flex.items-center.gap-2
   (when label [:label label])
   [:input.tabular-nums
    {:type "datetime-local"
     :class ["bg-white hover:bg-slate-50 hover:border-slate-300 h-[32px] px-2 leading-none outline-none text-slate-500 w-full text-xs"
             "border border-slate-200 rounded focus:ring-2 ring-indigo-500 ring-offset-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.04)]"]
     :value value
     :on-key-down (fn [e] (when (= (.-key e) "Enter") (on-value! (.. e
                                                                     -target
                                                                     -value))))
     :on-change (fn [e] (on-value! (.. e
                                       -target
                                       -value)))}]])

(defn week-nav [^js date on-change-day]
  [:<>
   [button {:variant :white
            :on-click #(on-change-day (.minus date #js {:days 7}))}
    [icon :double-arrow-left]]
   [native-date-picker nil (.toISODate date) (comp on-change-day DateTime.fromISO)]
   [button {:on-click #(on-change-day (DateTime.now))
            :variant (if (.hasSame date (DateTime.now) "week") :secondary :white)}
    (tr "Today")]
   [button {:variant :white
            :on-click #(on-change-day (.plus date #js {:days 7}))}
    [icon :double-arrow-right]]])

(def milliseconds-in-day (* 1000 60 60 24))

(defn format-date [date]
  (str/join "-" [(.getFullYear date)
                 (.. (js/String. (inc (.getMonth date))) (padStart 2 "0"))
                 (.. (js/String. (.getDate date)) (padStart 2 "0"))]))

(defn now []
  (js/Date.))

(defn add-days [date days]
  (let [d (js/Date.)
        time (.getTime date)]
    ;; XXX not robust to DST
    (.setTime d (+ time (* days milliseconds-in-day)))
    (format-date d)))

(defn date-range-picker
  ([{:as range :keys [after before on-change live?]}]
   (let [!selected (hooks/use-state :month)
         !after (hooks/use-state after)
         !before (hooks/use-state before)]
     [:div.flex.flex-row.gap-2
      [:button.text-xs.leading-none
       {:type :button
        :class (if (= @!selected :month)
                 ["bg-indigo-600" "text-white" "rounded-full" "px-3"]
                 ["text-indigo-600"])
        :on-click (fn [_]
                    (let [new-after (add-days (now) -31)
                          new-before (add-days (now) -1)]
                      (reset! !selected :month)
                      (reset! !after new-after)
                      (reset! !before new-before)
                      (when live? (on-change {:after new-after :before new-before}))))}
       (tr "last %1 days" 31)]
      [:button.text-xs.leading-none
       {:type :button
        :class (if (= @!selected :week)
                 ["bg-indigo-600" "text-white" "rounded-full" "px-3"]
                 ["text-indigo-600"])
        :on-click (fn [_]
                    (let [new-after (add-days (now) -7)
                          new-before (add-days (now) -1)]
                      (reset! !selected :week)
                      (reset! !after new-after)
                      (reset! !before new-before)
                      (when live? (on-change {:after new-after :before new-before}))))}
       (tr "last %1 days" 7)]
      [:button.text-xs.leading-none
       {:type :button
        :class (if (= @!selected :today)
                 ["bg-indigo-600" "text-white" "rounded-full" "px-3"]
                 ["text-indigo-600"])
        :on-click (fn [_]
                    (let [new-after (format-date (now))
                          new-before (format-date (now))]
                      (reset! !selected :today)
                      (reset! !after new-after)
                      (reset! !before new-before)
                      (when live? (on-change {:after new-after :before new-before}))))}
       (tr "Today")]
      [:div.flex.items-center.gap-2.border-l.border-slate-300.ml-2.pl-2.leading-none
       [:label.text-xs (tr :date-range/from)]
       [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow.tabular-nums
        {:type "date"
         :class "border-slate-300"
         :value @!after
         :on-change (fn [e]
                      (let [new-after (.. e -target -value)]
                        (reset! !selected nil)
                        (reset! !after new-after)
                        (when live? (on-change {:after new-after :before @!before}))))}]]
      [:div.flex.items-center.gap-2
       [:label.text-xs (tr :date-range/to)]
       [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow.tabular-nums
        {:type "date"
         :class "border-slate-300"
         :value @!before
         :on-change (fn [e]
                      (let [new-before (.. e -target -value)]
                        (reset! !selected nil)
                        (reset! !before new-before)
                        (when live? (on-change {:after @!after :before new-before}))))}]]
      (when-not live?
        [button
         {:variant :primary
          :size 1
          :loading? @deframe/!loading?
          :on-click (fn [_] (on-change {:after @!after :before @!before}))}
         (tr "Apply")])])))

(def dropdown-transition-ms 150)

(defn dropdown-portal [{:keys [align anchor class on-positioned side-offset]} & children]
  (let [!ref (hooks/use-ref)
        ^js a (when @anchor (.getBoundingClientRect @anchor))
        !pos (hooks/use-state (when a {:left (.-left a) :top (.-bottom a)}))]
    (hooks/use-effect
     (fn []
       (let [update-pos!
             (fn []
               (when (and @anchor @!ref)
                 (let [^js a (.getBoundingClientRect @anchor)
                       ^js dd (.getBoundingClientRect @!ref)
                       [vw vh] [(.-innerWidth js/window) (.-innerHeight js/window)]
                       offset (or side-offset 0)
                       left (case align
                              :center (- (+ (.-left a) (/ (.-width a) 2)) (/ (.-width dd) 2))
                              :end (- (.-right a) (.-width dd))
                              (.-left a))
                       top (let [below (+ (.-bottom a) offset)
                                 above (- (.-top a) (.-height dd) offset)]
                             (if (< (+ below (.-height dd)) vh)
                               below
                               above))]
                   #_(prn {:left left :top top :vw vw :vh vh
                           :V? (> (+ (.-bottom a) (.-height dd)) vh)
                           :H? (> (+ (.-left a) (.-width dd)) vw)
                           :dd-wh [(.-width dd) (.-height dd)]
                           :a-lt [(.-left a) (.-top a)]})
                   (reset! !pos {:left (-> left
                                           (max 0)
                                           (min (max 0 (- vw (.-width dd)))))
                                 :top (-> top
                                          (max 0)
                                          (min (max 0 (- vh (.-height dd)))))})
                   (when on-positioned
                     (on-positioned)))))
             ^js resize-observer (js/ResizeObserver. update-pos!)]
         (update-pos!)
         (.observe resize-observer @!ref)
         (.addEventListener js/window "resize" update-pos!)
         (.addEventListener js/window "scroll" update-pos! true) ; capture all, even in overflow containers
         (fn []
           (.disconnect resize-observer)
           (.removeEventListener js/window "resize" update-pos!)
           (.removeEventListener js/window "scroll" update-pos! true)))
       js/undefined)
     [@anchor @!ref])

    (react-dom/createPortal
     (r/as-element
      (into
       [:div
        {:ref !ref
         :class (class-vec "fixed" class)
         :style {:z-index (z-index :combobox-choices)
                 :pointer-events "auto"
                 :left (:left @!pos)
                 :top (:top @!pos)}}]
       children))
     js/document.body)))

(defn dropdown-link [opts & content]
  (into [link (assoc opts :class (class-vec "p-2 gap-3 flex hover:bg-slate-100 first-child:rounded-t-lg last-child:rounded-b-lg" (:class opts)))]
        content))

(def nav-dropdown-menu-class
  "min-w-[220px] overflow-hidden rounded-md border border-slate-300 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.18)]")

(def nav-dropdown-item-base-class
  "mx-1 flex w-auto items-center gap-2 rounded px-3 py-1 text-[12px] leading-5 no-underline transition-colors duration-150 border-0 bg-transparent text-left appearance-none")

(def nav-dropdown-item-enabled-class
  "text-slate-700 hover:bg-slate-50 hover:no-underline focus-visible:bg-slate-50 focus-visible:no-underline")

(def nav-dropdown-item-disabled-class
  "cursor-default text-slate-400")

(def nav-dropdown-note-class
  "px-3 py-1 text-[12px] leading-5 text-slate-400")

(defn nav-dropdown-link [opts & content]
  (let [disabled? (:disabled? opts)
        class (class-vec nav-dropdown-item-base-class
                         (if disabled?
                           nav-dropdown-item-disabled-class
                           nav-dropdown-item-enabled-class)
                         (:class opts))]
    (if disabled?
      (into
       [:div (-> opts
                 (dissoc :class :disabled? :href :on-click :target :rel)
                 (assoc :aria-disabled true
                        :class class))]
       content)
      (let [opts (-> opts
                     (dissoc :disabled? :class)
                     (assoc :class class))]
        (if (:href opts)
          (into [:a opts] content)
          (into [:button (assoc opts :type "button")] content))))))

(defn nav-dropdown-note [& content]
  (let [[opts content] (opts+content content)]
    (into
     [:div (-> opts
               (dissoc :as)
               (assoc :class (class-vec nav-dropdown-note-class (:class opts))))]
     content)))

(defn localstorage-toggle [opts label checked? & [key]]
  [dropdown-link (merge {:class "block text-xs gap-[6px] min-w-fit"
                         :on-click #(deframe/localstorage-assoc! key (not checked?))} opts)
   (if checked? [icon {:size 15} :check] [:div {:class "size-[15px]"}])
   label])

(defn dropdown [{:keys [align anchor-class button items items-class menu-class side-offset]}]
  (let [!anchor (hooks/use-ref)
        !open? (hooks/use-state false)
        !mounted? (hooks/use-state false)
        !positioned? (hooks/use-state false)
        !visible? (hooks/use-state false)]
    (hooks/use-effect
     (fn []
       (cond
         @!open?
         (do
           (reset! !mounted? true)
           (reset! !positioned? false)
           (reset! !visible? false)
           nil)

         @!mounted?
         (do
           (reset! !visible? false)
           (let [timeout-id (js/setTimeout #(reset! !mounted? false) dropdown-transition-ms)]
             #(js/clearTimeout timeout-id)))

         :else
         (do
           (reset! !visible? false)
           nil)))
     [@!open? @!mounted?])
    (hooks/use-effect
     (fn []
       (when (and @!open? @!mounted? @!positioned?)
         (let [animation-frame-id (js/requestAnimationFrame #(reset! !visible? true))]
           #(js/cancelAnimationFrame animation-frame-id))))
     [@!open? @!mounted? @!positioned?])
    (hooks/use-effect (fn []
                        (let [on-hide (fn [e] ; useCapture clickaway handler fires before rerender
                                        (js/requestAnimationFrame #(reset! !open? false)))
                              on-keydown (fn [^js e] (when (= "Escape" (.-key e))
                                                       (reset! !open? false)))]
                          (when @!open?
                            (js/document.addEventListener "click" on-hide true)
                            (js/document.addEventListener "keydown" on-keydown)
                            #(do (js/document.removeEventListener "click" on-hide true)
                                 (js/document.removeEventListener "keydown" on-keydown)))))
                      [@!open?])
    [:<>
     [:div {:ref !anchor
            :class anchor-class
            :on-click (fn [e] (.stopPropagation e) (swap! !open? not))}
      button]
     (when @!mounted?
       [dropdown-portal {:align align
                         :anchor !anchor
                         :on-positioned #(reset! !positioned? true)
                         :class (class-vec (when-not menu-class "rounded-lg bg-white shadow-lg")
                                           "origin-top-left transition-[opacity,transform] duration-150 ease-out will-change-[opacity,transform]"
                                           (case align
                                             :center "origin-top"
                                             :end "origin-top-right"
                                             "origin-top-left")
                                           (when-not @!visible? "pointer-events-none -translate-y-1 scale-[0.98] opacity-0")
                                           (when @!visible? "translate-y-0 scale-100 opacity-100")
                                           menu-class)
                         :side-offset side-offset}
        [:div {:class (class-vec "flex max-h-96 flex-col overflow-y-auto" items-class)} items]])]))

(defn with-label
  "A higher-order component that renders a label above its child component.
   The child component will automatically stretched to full width. This is
   supposed to be used in a container that already has a defined width. E.g.
   `filter-bar`."
  [label & content]
  [:div
   [:label.block.text-sm.leading-5.font-medium.mb-1 label]
   (into [:div.form-with-label] content)])

(defn explain-link [& content]
  (let [[opts content] (opts+content content)
        {:keys [path class data-testid]} opts]
    (into
     [:a (cond-> {:href (academy-url (or path "/"))
                  :target "_blank"
                  :class (class-vec "inline-flex items-center gap-2 text-[12px] leading-[16px] text-indigo-700 hover:text-indigo-800 hover:underline transition-colors"
                                    class)}
           data-testid (assoc :data-testid data-testid))]
     [[:span {:class "flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-sm border border-[#C2C2C2] bg-white shadow-[0_1px_1px_rgba(0,0,0,0.1)]"}
       [icon {:size 16 :class "text-slate-700"} :academy]]
      [:span (into [:<>] content)]])))

(defn vehicle-link [{:as event :keys [vehicle/model vehicle/vin]}]
  [:a.text-indigo-600.hover:underline.hover:text-indigo-700.transition-all.flex
   {:href (router/path '[compound :compound-name vehicle :fahrzeug-id] (assoc event :fahrzeug-id vin))}
   [:span.mr-3 {:class "w-[124px]"} vin] model])

(defn event-item [{:as event :event/keys [kind timestamp]}]
  (let [link [:a.text-indigo-600.hover:underline.hover:text-indigo-700.transition-all.mx-2
              {:href (router/path '[compound :compound-name vehicle :fahrzeug-id] (assoc event :fahrzeug-id (:vehicle/vin event)))}
              (:vehicle/vin event)]]
    [:li.flex.relative
     [:div.flex.mr-3.mt-1.items-center.justify-center
      (case kind
        :fahrzeug/parked park-icon
        :fahrzeug/entered icons/entered
        :fahrzeug/exited icons/exited
        :service/completed icons/check)
      [:div.text-xs.ml-2
       [:span (time-format/->str timestamp) " - "]
       (case kind
         :fahrzeug/parked
         [:span (tr "Vehicle parked") link]
         :fahrzeug/entered
         [:span (tr "Vehicle entered") link]
         :fahrzeug/exited
         [:span (tr "Vehicle exited") link]
         :service/completed
         [:span
          (:service/code event)
          link]
         [:div])]]]))

(defn event-group-item [{:as event :event/keys [kind timestamp events] :keys [make/name]}]
  (r/with-let [!expanded? (r/atom false)]
    [:li.flex.relative
     [:div.mr-1
      {:class "mt-[8px] text-indigo-600 w-[20px] h-[20px] rounded-full hover:bg-slate-200 cursor-pointer flex items-center justify-center"
       :on-click #(swap! !expanded? not)}
      [icon {:size 18 :class (str "transition-all " (if @!expanded? "rotate-90" "rotate-0"))} :chevron-right]]
     [:div.flex-shrink-0.mr-3.mt-1
      (case kind
        :fahrzeug/parked park-icon
        :fahrzeug/entered icons/entered
        :fahrzeug/exited icons/exited
        :service/completed icons/check
        (throw (ex-info (str "unhandled event kind: " kind) {:event event})))]
     [:div.flex-auto.text-xs.transition-all
      [:div {:class (when @!expanded? "font-bold")}
       [:span.text-slate-500.block (time-format/->str timestamp)]
       (case kind
         :fahrzeug/parked
         [:span.block
          {:on-click #(swap! !expanded? not)}
          [:span.text-indigo-600.hover:underline.cursor-pointer
           (tr "%1 vehicles were parked" (count events))]
          "."]
         (:fahrzeug/entered :fahrzeug/exited)
         [:span.block [:span.text-indigo-600.hover:underline.cursor-pointer
                       {:on-click #(swap! !expanded? not)}
                       (count events) " " (:make/name event) " " (tr "Vehicles") " "]
          (case kind
            :fahrzeug/entered (tr "have entered the compound.")
            :fahrzeug/exited (tr "have exited the compound."))]
         :service/completed
         [:span.block
          {:on-click #(swap! !expanded? not)}
          (:service/code event) " "
          [:span.text-indigo-600.hover:underline.cursor-pointer
           (tr "was completed for %1 vehicles" (count events))]
          "."]
         [:div])]
      (when @!expanded?
        (into [:ul.mt-2]
              (map (fn [{:as vehicle-event :keys [event/timestamp]}]
                     [:li.py-1.leading-none.flex
                      {:class "text-[11px]"}
                      [:span.text-slate-500.mr-2 {:class "w-[105px]"} (time-format/->str timestamp)]
                      [vehicle-link (merge event vehicle-event)]]))
              events))]
     [:span.rounded.bg-slate-200.absolute {:class "w-[2px] left-[37px] top-[37px] -bottom-[13px]"}]]))

(defn grouped-events-list [events-by-hour]
  [:div.not-prose
   (into
    [:ul.flex.flex-col.gap-3.text-sm]
    (map (fn [event] [event-group-item event]))
    events-by-hour)])

(def requested?
  (apply some-fn schema/requested-keys))

(declare badge)

(def service-label service.label/service-label)
(def order-service-label service.label/order-service-label)
(def template-quantity service.label/template-quantity)

(defn service+details [service]
  [:div.flex.gap-1.items-center
   (:service/code service)
   (when (or (:service/quantity service) (requested? service))
     [badge {:variant :white-blue} (template-quantity service)])
   (when (:service/detail service)
     [badge {:variant :white-blue} (:service/detail service)])])

(defn event-undone-text [undone-event]
  (as-> (case (:event/kind undone-event)
          :service.event/ordered (tr "order")
          :service.event/canceled (tr "cancellation")
          :service.event/undone (tr "undo")
          nil) what
    (str what (when what " ") (tr "has been undone"))))

(defn strikethrough-when-undone [event text]
  (cond->> text (:event/undone-at event) (vector :s)))

(defn show-form-modal!
  ([lambda-sym]
   (show-form-modal! lambda-sym {}))
  ([lambda-sym args]
   (show-form-modal! lambda-sym args nil))
  ([lambda-sym args {:keys [on-pop]}]
   (let [view-id (str (random-uuid))
         result-path [:current-route/transient :ductile.ui.view-stack/view-stack-results view-id]]
     (deframe/route-update! :ductile.ui.view-stack/view-stack
                            (fnil conj [])
                            (cond-> {:view/type :form
                                     :view/id view-id
                                     :view/result-path result-path
                                     :view/lambda-sym lambda-sym
                                     :view/lambda-args args}
                              on-pop (assoc :view/on-pop on-pop)))
     (deframe/call-lambda {:result-path result-path} lambda-sym args)
     ;; return nil instead of promise to avoid button showing pending spinner (the modal opens immediately and shows its own spinner)
     nil)))

(defn shortcut
  ([ks]
   (shortcut {} ks))
  ([opts ks]
   [:span {:class (case (:variant opts)
                    :tooltip "text-indigo-300"
                    "text-mauve9 group-data-[highlighted]:text-white group-data-[disabled]:text-mauve8")}
    (->> ks (map platform-key) (map str/capitalize) (str/join " "))]))

(defn tooltip [opts trigger]
  [:> Tooltip/Provider
   [:> Tooltip/Root {:delay-duration 0}
    [:> Tooltip/Trigger {:as-child true}
     trigger]
    [:> Tooltip/Portal
     [:> Tooltip/Content {:class (class-vec "data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] px-[15px] py-[10px] leading-none will-change-[transform,opacity] text-[13px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] text-center"
                                            (case (:variant opts)
                                              :secondary "bg-indigo-200 text-indigo-800"
                                              :white "bg-white text-black"
                                              "bg-indigo-700 text-white"))
                          :style {:z-index (z-index :tooltip)}
                          :side (or (some-> (:side opts) name) "bottom")
                          :side-offset 5}
      [:div.flex.gap-2
       (:content opts)
       (when (:shortcut opts)
         [shortcut {:variant :tooltip} (:shortcut opts)])]
      [:> Tooltip/Arrow {:class (case (:variant opts)
                                  :white "fill-white"
                                  "fill-indigo-700")}]]]]])

(defc edit-diff-table [before after]
  (let [inspect-value (resolve 'ductile.ui.fahrzeug/inspect-value)]
    [:table.border.border-slate-300.rounded.border-separate.border-spacing-0
     [:thead [:tr.leading-6
              [:th.px-3.border-b.border-slate-300 (tr "Attribute")]
              [:th.px-3.border-b.border-slate-300 (tr "Before")]
              [:th.px-3.border-b.border-slate-300 (tr "After")]]]
     (into [:tbody]
           (map (fn [attr]
                  [:tr.leading-6
                   [:td.px-3 (tr attr)]
                   [:td.px-3 (inspect-value (get before attr))]
                   [:td.px-3 (inspect-value (get after attr))]]))
           (sort (distinct (concat (keys after) (keys before)))))]))

(defn event-summary [{:keys [span-block?]} {:as event :event/keys [target kind] :billing/keys [invoice]}]
  (let [!open? (hooks/use-state false)
        span (if span-block? :span.block :span)]
    (case kind
      :fahrzeug/parked [span (tr "Vehicle") " "
                        [:span.font-bold (tr "was parked at %1" (str/join "/"
                                                                          (remove nil? ((juxt :parking-spot/zone
                                                                                              :parking-spot/row
                                                                                              :parking-spot/place) event))))]]
      (:fahrzeug/entered :fahrzeug/exited)
      [span (tr "Vehicle") " "
       [:span.font-bold
        (case kind
          :fahrzeug/entered (tr "has entered the compound.")
          :fahrzeug/exited (tr "has exited the compound."))]]

      (:dispo/withdrawal :dispo/delivered)
      [span (tr "Vehicle") " "
       [:span.font-bold
        (case kind
          :dispo/withdrawal (tr "was picked up by transport.")
          :dispo/delivered (tr "was delivered to final destination."))]]

      :service.event/invoiced
      (strikethrough-when-undone event [span [:span.font-bold (service-label event)] " " (tr "was invoiced on") " " [:a.text-indigo-600.py-1.hover:bg-indigo-50.cursor-pointer {:href (str "/invoice/" (:invoice/invoice-number invoice))} (:invoice/invoice-number invoice)] "."])
      :service.event/canceled
      (strikethrough-when-undone event [span [:span.font-bold (service-label event)] " " (tr "was cancelled") "."])
      :service.event/completed
      (strikethrough-when-undone event [span [:span.font-bold (service-label event)] " " (tr "was completed") "."])
      :service.event/ordered
      (strikethrough-when-undone event [span [:span.font-bold (service-label event)] " " (tr "was ordered") "."])
      :service.event/edited
      (strikethrough-when-undone event
                                 [:div.mx-1 [span [:span.font-bold (service-label event)] " " (tr "was edited") "."
                                             " " [link {:on-click #(swap! !open? not)}
                                                  (if @!open? (tr "Hide details") (tr "Show details"))]

                                             (when @!open?
                                               [:div.p-2
                                                [edit-diff-table (:service.edit/before event) (:service.edit/after event)]])]])
      :service.event/undone
      (strikethrough-when-undone event [span [:span.font-bold (service-label target)] " " (event-undone-text target) "."])

      [span [:span.font-bold (:service/code event)] " " (tr "unknown event %1" kind) "."])))

(defn event-list-item [{:as event :event/keys [target kind timestamp]}]
  [:div.px-5.hover:bg-indigo-50.group.transition-all
   [:li.flex.relative.items-center.py-1
    [:div.flex-shrink-0.mr-3.mt-1
     (case kind
       :fahrzeug/parked park-icon
       :fahrzeug/entered icons/entered
       :fahrzeug/exited icons/exited
       :service.event/invoiced invoice-event-icon
       :service.event/completed icons/check
       :service.event/ordered icons/created
       :service.event/canceled cancelled-icon
       :service.event/undone undone-icon
       :service.event/edited edit-icon
       ;; TODO better icon
       :dispo/withdrawal icons/exited
       ;; TODO better icon
       :dispo/delivered icons/check)]
    [:div.flex-auto.text-xs.transition-all
     [:div
      [:span.text-slate-500.block (time-format/->str timestamp)
       (when-some [user-email (-> event :ductile/user :user/email)]
         (when-current-user-scope
          :scope/service-event-user
          (str " • " user-email)))
       (when-let [provider (-> event :edi/provider)]
         (str " • " (tr provider)))]

      [debug (:ductile/id event)]
      (event-summary {:span-block? true} event)]]
    [:div.flex.gap-2
     (when (:event/undoable? event)
       [:div.hidden.group-hover:flex
        [button {:variant :destructive :size 1
                 :on-click #(deframe/call-lambda 'ductile.workflow/undo-event event)}
         (tr "Undo event")]])

     (when (:event/editable? event)
       [:div.hidden.group-hover:flex
        [button {:variant :secondary :size 1
                 :on-click #(show-form-modal! 'ductile.workflow/edit-event-form (select-keys event [:ductile/id]))
                 :data-testid (str "edit-" (str/lower-case (:service/code event)))}
         (tr "Edit")]])]
    [:span.rounded.bg-slate-200.absolute.group-last:hidden {:class "w-[2px] left-[13px] top-[44px] -bottom-[18px]"}]]])

(defn events-list [events]
  [:div.not-prose
   (into
    [:ul.flex.flex-col.gap-3.text-sm.py-3]
    (map (fn [event] [event-list-item event]))
    events)])

(defn do-logout! []
  (prn :logout!)
  (-> (http/$logout)
      (.then (fn [response]
               (deframe/reset-current-user!)
               (router/navigate-to "/login")))))

(def support-ticket-url
  "https://helpdesk.ars-altmann.de")

(defn user-dropdown-identity-label [{:user/keys [email name]}]
  (cond
    (and (seq name) (seq email)) (str name " (" email ")")
    (seq email) email
    (seq name) name
    :else (tr "No email provided.")))

(defn user-dropdown [current-user]
  (let [academy-access? (util/has-required-scope? :scope/academy)]
    [dropdown
     {:align :end
      :anchor-class "flex h-full items-center"
      :items-class "py-1"
      :menu-class nav-dropdown-menu-class
      :side-offset 10
      :button [:button {:tab-index -1
                        :class "flex items-center gap-1 rounded transition-opacity group cursor-pointer flex-shrink-0 focus-visible:ring-2 focus-visible:ring-indigo-500 ring-offset-4"}
               (if-let [initials (seq (cond
                                        (:user/name current-user) (mapv first (str/split (:user/name current-user) #" "))
                                        (:user/email current-user) (mapv first (-> (:user/email current-user)
                                                                                   (str/split #"@")
                                                                                   first
                                                                                   (str/split #"\.|\-|\_")))))]
                 [:div {:class "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 group-hover:bg-indigo-100 group-active:bg-indigo-200"}
                  [:div {:class "leading-none font-bold uppercase tracking-wide text-indigo-500"
                         :style {:font-size (if (< (count initials) 3) "11px" "10px")}}
                   (str/join initials)]]
                 [:div {:class "text-xl"} "🙂"])
               [icon {:size 12} :chevron-down]]
      :items
      [:div {:class "flex flex-col items-stretch"}
       [nav-dropdown-note {:class "break-all"}
        (user-dropdown-identity-label current-user)]
       (when academy-access?
         [:<>
          [:div {:class "my-1 h-px bg-slate-200"}]
          [nav-dropdown-link {:href (academy-url "/")
                              :rel "noreferrer"
                              :target "_blank"}
           [icon {:size 14 :class "text-slate-500"} :academy]
           [:span (tr "Guides (Ductile Academy)")]
           [:span {:class "ml-auto text-slate-400"} [icon {:size 12} :open-in-new-window]]]
          [nav-dropdown-link {:href support-ticket-url
                              :rel "noreferrer"
                              :target "_blank"}
           [icon {:size 14 :class "text-slate-500"} :question-mark-circled]
           [:span (tr "Open Support Ticket")]
           [:span {:class "ml-auto text-slate-400"} [icon {:size 12} :open-in-new-window]]]])
       [:div {:class "my-1 h-px bg-slate-200"}]
       [nav-dropdown-link {:on-click #(.reload js/window.location)}
        [icon {:size 14 :class "text-slate-500"} :reload]
        [:span (tr "Reload app")]]
       [nav-dropdown-link {:on-click do-logout!}
        [icon {:size 14 :class "text-slate-500"} :exit]
        [:span (tr "Log out")]]]}]))

(defn measure-text-width [style-of-el text]
  (let [font (.. js/window (getComputedStyle style-of-el nil) (getPropertyValue "font"))
        ^js canvas (js/document.createElement "canvas")
        ^js ctx (.getContext canvas "2d")
        _ (set! (.-font ctx) font)]
    (.. ctx (measureText text) -width)))

(defc compound-select [{:as current-user :user/keys [selected-compound compounds]}]
  (let [!ref (hooks/use-ref)
        !width (use-localstorage ::compound-select-width nil)
        value (or selected-compound (first (sort compounds)))]
    (hooks/use-effect (fn [] (reset! !width (+ 50 (measure-text-width @!ref value)))) [value @!ref])
    [:div.flex-shrink
     [select {:ref !ref
              :on-change (fn [value]
                           (p/let [{:keys [selected-compound]} (http/$lambda 'ductile.lambda/handle-select-compound {:compound/name value})]
                             (deframe/select-compound! selected-compound)
                             (router/navigate-to (router/same-route-different-compound selected-compound))))
              :value value
              :aria-label (tr "Select compound")
              :data-testid "select-compound"
              :choices (map (fn [compound] {:ui/value compound
                                            :ui/label compound})
                            (sort compounds))
              :placeholder (str (tr "Select a compound") "…")
              :left [:div.text-indigo-700.ml-2 {:class "ml-[0.3rem]"} [icon :sewing-pin-filled]]
              :size 1
              :tab-index -1
              :style {:width @!width}
              :class "pl-5 pr-4 text-indigo-700"}]]))

(defn top-bar [& content]
  (when-let [current-user @deframe/!current-user]
    [:div.bg-white.border-b.px-5.border-b.flex.items-center.flex-shrink-0.relative.shadow.justify-between.relative
     [:a.mr-3.py-3 {:class "-ml-0.5" :href "/"}
      [:img {:src (maybe-add-host "/images/logo.svg") :width 57}]]
     (when (< 1 (count (:user/compounds @deframe/!current-user)))
       [:<>
        [compound-select @deframe/!current-user]
        (when-not (empty? content)
          [:div.mx-3.bg-slate-200 {:class "w-[1px] h-[33px]"}])])
     (into [:div.flex-auto] content)
     [user-dropdown current-user]]))


(def envs
  {:dev {:label "Dev" :scope :scope/developer :host "dev.ductile.de:8888" :bar-color "#a5b4fd" :class "text-indigo-800"}
   :ars-demo {:label "Demo" :host "ars-demo.ductile.de" :bar-color "rgb(249 223 121)" :class "text-yellow-800"}
   :ars-staging {:label "Staging" :host "ars-staging.ductile.de" :bar-color "rgb(79, 219, 193)" :class "text-teal-900"}
   :ars {:label "Production"
         :host "ars.ductile.de"
         :bar-color "oklch(60% 0.21 27)"
         :class "text-white"
         :bar-style {:background-image "repeating-linear-gradient(-35deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3) 30px, oklch(60% 0.21 27) 30px, oklch(60% 0.21 27) 60px)"}}})

(defc read-env! []
  (get envs (keyword (first (str/split (.. js/location -hostname) #"\.")))))

(defc debug-mode-indicator []
  (when (debug-mode?)
    [:div.text-xs.font-bold.text-indigo-600.cursor-pointer.hover:text-indigo-400.select-none {:on-click toggle-debug-mode!} "Debug"]))

(def show-env-bar?
  (deframe/!localstorage-get ::show-env-bar? true))

(defn env-bar []
  (let [env (read-env!)]
    (when (if (util/has-required-scope? :scope/developer)
            @show-env-bar?
            (not= "ars.ductile.de" (:host env)))
      [:div.print:hidden.sticky.top-0 {:style (merge {:background-color (:bar-color env) :height 8 :z-index (z-index :env-bar)} (:bar-style env))}])))

(defc environment-indicator []
  (when-let [env (read-env!)]
    (if (util/has-required-scope? :scope/developer)
      [dropdown
       {:align :end
        :anchor-class "flex h-full items-center"
        :items-class "py-1"
        :menu-class nav-dropdown-menu-class
        :side-offset 10
        :button [:div {:class "flex cursor-pointer flex-col justify-center select-none font-semibold hover:opacity-90"}
                 [:div {:class (class-vec "flex items-center rounded-full py-[0.35rem] text-xs"
                                          (if @show-env-bar?
                                            (str (:class env) " px-3")
                                            "text-slate-500 hover:text-slate-400"))
                        :style (when @show-env-bar? {:background-color (:bar-color env)})}
                  [:span {:class "pr-1"} (:label env)]
                  [icon {:size 12} :chevron-down]]]
        :items
        (conj
         (into
          [:div {:class "flex flex-col"}]
          (comp
           (remove (comp nil? val))
           (map (fn [[_ {:keys [bar-color host label scope]}]]
                  (let [current-host (str js/window.location.host)
                        current? (= host current-host)
                        disabled? (or current?
                                      (and scope (not (util/has-required-scope? scope))))]
                    [nav-dropdown-link
                     {:class "justify-start"
                      :disabled? disabled?
                      :href (when-not disabled?
                              (str/replace (str js/window.location) current-host host))}
                     [:span {:class "flex items-center gap-2"}
                      [:span {:class "flex w-3 justify-center"}
                       (when current?
                         [icon {:size 11 :class "text-slate-400"} :check])]
                      [:span {:class "h-2 w-2 rounded-full"
                              :style {:background-color bar-color
                                      :opacity (if disabled? 0.45 1)}}]
                      [:span label]]]))))
          envs)
         [:div {:class "my-1 h-px bg-slate-200"}]
         [nav-dropdown-link {:class "justify-start"
                             :on-click (fn [] (deframe/localstorage-assoc! ::show-env-bar? (not @show-env-bar?)))}
          [:span {:class "flex items-center gap-2"}
           [:span {:class "flex w-3 justify-center"}
            (when @show-env-bar?
              [icon {:size 11 :class "text-slate-400"} :check])]
           [:span {:class "font-medium"} (tr "Show bar")]]])}]


      (when-not (= "ars.ductile.de" (:host env))
        [:div {:class "flex flex-col justify-center select-none font-semibold"}
         [:div {:class [(:class env) "flex items-center rounded-full px-3 py-[0.35rem] text-xs"]
                :style {:background-color (:bar-color env)}}
          [:span {:class "pr-1"} (:label env)]]]))))

(defn tabs [& content]
  (let [[opts content] (opts+content content)]
    (into [:> Tabs/Root
           (merge {:class-name ""}
                  opts)]
          content)))

(defn tabs-list [& content]
  (let [[opts content] (opts+content content)]
    [:> Tabs/List
     (merge {:class-name "flex"} opts)
     (into [:div.flex.rounded.border.border-slate-300] content)]))

(defn text-tabs-list [& content]
  (let [[opts content] (opts+content content)]
    [:> Tabs/List
     (merge {:class-name "flex"} opts)
     (into [:div.flex.rounded.border-1.border-slate-300] content)]))

(defn tab-opts->classes [{:keys [size variant disabled?] :as props}]
  (concat (button-opts->classes (assoc props :variant :white))
          ["bg-white" "hover:bg-indigo-50" "data-[state=active]:bg-indigo-600"
           "data-[state=active]:text-white" "text-indigo-700" "rounded-none" "first:rounded-l" "last:rounded-r" "border-r" "first:border-r-0" "border-slate-300"]
          (when disabled? ["opacity-70 pointer-events-none"])))

(defn text-tab [& content]
  (let [[opts content] (opts+content content)]
    (into
     [:> Tabs/Trigger (merge {:class ["data-[state=active]:font-bold" "text-indigo-700" "border-0" "bg-transparent" "text-xs" "hover:underline"]} opts)]
     content)))

(defn tab [& content]
  (let [[opts content] (opts+content content)]
    (into
     [:> Tabs/Trigger (merge {:class (tab-opts->classes (merge {:size 2} opts))} opts)]
     content)))

(defn tab-content [& content]
  (let [[opts content] (opts+content content)]
    (into [:> Tabs/Content opts] content)))

(defn legacy-sortable [opts content]
  (let [!el (hooks/use-ref nil)]
    (hooks/use-effect (fn [] (let [sort-el (first (Sortable @!el (clj->js (merge {:forcePlaceholderSize true}
                                                                                 (dissoc opts :on-sort-update)))))]
                               (.addEventListener sort-el "sortupdate" (:on-sort-update opts #())))) [content])
    ^{:key content}
    [:div {:ref !el} (doall content)]))

(defn toast-provider [& content]
  (into [:> Toast/Provider {:swipe-direction :right}
         [:> Toast/Viewport {:class-name "[--viewport-padding:_25px] fixed bottom-0 right-0 flex flex-col p-[var(--viewport-padding)] gap-[10px] w-[390px] max-w-[100vw] m-0 list-none outline-none print:hidden"
                             :style {:z-index (z-index :toast)}}]
         content]))

(defn toast-action [opts]
  (into [(:as opts :button)
         (merge {:class (str "rounded px-2 py-1 border text-[12px] whitespace-nowrap flex-shrink-0 "
                             (case (:variant opts)
                               :success "bg-green2 hover:bg-green3 border-green7 hover:border-green8 text-green11"
                               "bg-indigo-50/50 hover:bg-indigo-50 border-indigo-300 hover:border-indigo-400 text-indigo-700"))
                 :on-click #(deframe/dissoc! :toast)}
                (dissoc opts :variant :description :action :as))]
        (:label opts)))

(defn toast [{:keys [action description title variant duration]}]
  [:> Toast/Root
   {:class-name (str "bg-white rounded-md shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] p-[15px]  data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut relative "
                     (case variant
                       :success "border-2 border-green7 "
                       :error "border-2 border-red7"
                       nil))
    :duration (or duration 5000)
    :on-open-change #(deframe/dissoc! :toast)}
   [:div.flex-auto
    (when title
      [:> Toast/Title {:data-testid "toast-title"
                       :class-name (str "mb-[5px] text-[15px] font-bold "
                                        (case variant
                                          :success "text-green9 "
                                          :error "text-red9"
                                          "text-slate12 "))} title])
    (when description
      [:> Toast/Description {:class-name "m-0 text-slate11 text-[13px] leading-[1.3]"}
       description])]
   (when action
     [:div.flex.items-center.justify-end
      [toast-action action]])
   [:> Toast/Close {:class-name "absolute top-2 right-2 text-slate8"}
    [icon :cross-2]]])

(defn badge-opts->classes [{:keys [size variant class]}]
  (class-vec
   ["rounded-full" "gap-1" "font-semibold" "tabular-nums" "inline-flex" "items-center" "justify-center" "text-center" "leading-none" "flex-shrink-0"]
   (get {1 ["min-h-3" "min-w-4" "text-[10px]" "px-[5px]" "py-[2px]"]
         2 ["min-h-5" "min-w-5" "text-xs" "px-[6px]" "py-0"]
         :unsized []} (or size 2))
   (get {:primary ["bg-indigo-500" "text-white"]
         :secondary ["bg-indigo-100" "text-indigo-800"]
         :green ["bg-green-500" "text-white" "border-green-500"]
         :red ["bg-red-500" "text-white"]
         :orange ["bg-orange-400" "text-white"]
         :blue ["bg-indigo-500" "text-white"]
         :gray ["bg-slate-200/70" "text-slate-800" "border" "border-slate-400"]
         :yellow ["bg-yellow-50" "text-yellow-600" "border" "border-yellow-400"]
         :white-green ["bg-white" "text-green-600" "border" "border-green-400"]
         :white-blue ["bg-white" "text-indigo-500" "border" "border-indigo-400"]
         :white-gray ["bg-white" "text-gray-600" "border" "border-gray-300"]} (or variant :green))
   class))

(defn badge [opts & children]
  (let [{:as opts :keys [on-close] :on-close/keys [data-testid]} (with-events opts)]
    [:div
     (merge (dissoc opts :on-close/data-testid)
            {:class (badge-opts->classes opts)})
     (into [:<>] children)
     (when on-close
       [:button.flex-shrink-0.ml-0.5.h-4.w-4.rounded-full.inline-flex.items-center.justify-center.focus:text-white.focus:outline-none
        (cond-> {:type :button
                 :on-click on-close
                 :class "hover:bg-white/50 hover:backdrop-saturate-200 hover:opacity-90 focus:bg-black/30"}
          data-testid (assoc :data-testid data-testid))
        [:svg.h-2.w-2 {:stroke "currentColor" :fill "none" :viewBox "0 0 8 8"}
         [:path {:stroke-linecap "round" :stroke-width "1.5" :d "M1 1l6 6m0-6L1 7"}]]])]))

(defn transport-status-badge [status]
  (let [bg-class (case status
                   :transport.status/in-planning "bg-slate-500"
                   :transport.status/awaiting-compound "bg-sky-500"
                   :transport.status/unloading-not-started "bg-sky-500"
                   :transport.status/partially-unloaded "bg-orange-500"
                   :transport.status/partially-prepared "bg-orange-500"
                   :transport.status/fully-prepared "bg-green-500"
                   :transport.status/fully-unloaded "bg-green-500"
                   :transport.status/ready-for-pickup "bg-blue-500"
                   :transport.status/exited "bg-purple-500"
                   :transport.status/deleted "bg-black"
                   "bg-slate-500")]
    [:span.rounded-full.px-2.py-1.font-bold.text-white.whitespace-nowrap
     {:class ["text-[11px]" bg-class]}
     (tr (or status "Unknown"))]))

(defc button-opts->classes [{:keys [size variant disabled? loading? class]}]
  (class-vec
   ["inline-flex" "flex-shrink-0" "items-center" "justify-center" "focus-visible:ring-2" "ring-indigo-500" "ring-offset-2" "font-medium" "leading-none" "rounded" "border"]
   (get {0 ["px-1" "h-[26px]" "text-[11px]" "gap-0.5"]
         1 ["px-2" "h-[30px]" "text-[12px]" "gap-1"]
         2 ["px-2" "h-[34px]" "text-xs" "gap-1"]
         3 ["px-3" "h-[40px]" "text-sm" "gap-2"]
         4 ["px-4" "h-[44px]" "text-base" "gap-3"]
         :unsized []} (or size 2))
   (if-not (or loading? disabled?) ; normal
     (get {:primary ["text-white" "bg-indigo-500" "hover:shadow-sm" "hover:border" "hover:border-indigo-800" "hover:bg-indigo-700" "active:bg-indigo-500" "border-transparent"]
           :secondary ["text-indigo-800" "bg-indigo-100" "hover:bg-indigo-200" "hover:shadow-sm" "active:bg-indigo-300" "border-indigo-400"]
           :white ["text-indigo-700" "bg-white" "hover:bg-indigo-50" "hover:shadow-sm" "active:bg-indigo-100" "border-indigo-300"]
           :destructive ["text-red-700" "bg-red-100" "hover:bg-red-200" "hover:shadow-sm" "hover:border" "hover:border-red-300" "active:bg-red-300" "active:text-red-800" "border-red-400"]
           :destructive-white ["text-red-700" "bg-white" "hover:shadow-sm" "hover:bg-red-50" "hover:border-red-300" "active:bg-red-200" "active:text-red-800" "active:border-red-300" "active:text-red-700" "border-red-400"]}
          (or variant :secondary))
     (get {:primary ["text-white" "bg-indigo-300" "border-transparent"] ; disabled
           :secondary ["text-indigo-300" "bg-indigo-100" "border-indigo-300"]
           :white ["text-indigo-300" "bg-white" "border-indigo-200"]
           :destructive ["text-red-300" "bg-red-100" "border-red-200"]
           :destructive-white ["text-red-200" "bg-white" "border-red-200"]}
          (or variant :secondary)))
   (when disabled? "cursor-not-allowed")
   class))

(defn button [& content]
  (let [!disabled-while-pending? (hooks/use-state false)
        [opts content] (opts+content content)
        {:keys [as on-click loading? disabled? href type]} (with-events opts)
        opts (-> opts
                 (assoc :disabled? (or loading? disabled? @!disabled-while-pending? (:disabled opts))
                        :loading? (or loading? @!disabled-while-pending?)
                        :on-click (fn [e]
                                    (let [^js result (cond-> e on-click on-click)]
                                      (when (and result (.-finally result))
                                        (reset! !disabled-while-pending? true)
                                        (.finally result #(reset! !disabled-while-pending? false)))
                                      result)))
                 (cond-> (not href)
                   (assoc :type (cond type type
                                      on-click :button
                                      :else :submit)))
                 (#(assoc %
                          :class (button-opts->classes %)
                          :disabled (:disabled? %)))
                 (dissoc :as :color :variant :size :loading? :disabled?))]
    [(if href :a (or as :button))
     opts
     (into [:<>] content)
     (when (or loading? @!disabled-while-pending?)
       [spinner {:size 16 :class "ml-1"}])]))

(def nbsp "\u00A0")

(defn data-list [& content]
  [:div.not-prose.text-sm
   [:table
    (into [:tbody] content)]])

(defn data-list-item [& content]
  (let [[opts content] (opts+content content)]
    (into [:tr.group
           {:class (case (:variant opts)
                     :align-top ["align-top"]
                     "align-middle")}]
          content)))

(defn data-list-separator [& content]
  [:<>
   [:tr
    [:td.pt-6.pb-2 {:col-span 2}
     (into [:div.font-bold.border-b.pb-1] content)]]])

(defn data-list-key [& content]
  (into [:th.text-left.pr-6.font-normal.text-slate11.py-1.group-first:pt-0.group-last:pb-0] content))

(defn data-list-value [& content]
  (into [:td.text-left.py-1.group-first:pt-0.group-last:pb-0] content))

(defn data-list-empty-value [& content]
  (into [:span.text-slate9] content))

(defn panel [& content]
  (let [[opts content] (opts+content content)]
    (into [:div.rounded.flex
           {:class (concat ["bg-white/60"]
                           (get {:container ["max-w-5xl" "w-full" "mx-auto"]
                                 :full ["w-full"]}
                                (:variant opts :container)))}]
          content)))

(defn panel-sidebar [& content]
  (into [:div.p-6.flex-shrink-0.first:rounded-l {:class "bg-white/60"}] content))

(defn panel-content [& content]
  (into [:div.p-6.flex-auto.overflow-auto.first:rounded-l.last:rounded-r
         {:class "bg-white"}]
        content))

(defn modal-portal [opts & content]
  (let [{:keys [open? on-close]} (with-events opts)]
    (hooks/use-effect
     (fn []
       (let [on-key (fn [^js e]
                      (when (= "Escape" (.-key e))
                        (prn ::esc open?)
                        (.stopPropagation e)
                        (when open? (on-close))))]
         (js/document.addEventListener "keydown" on-key)
         (when open? (js/document.body.classList.add "overflow-hidden"))
         #(do (js/document.removeEventListener "keydown" on-key)
              (js/document.body.classList.remove "overflow-hidden"))))
     [open? on-close])
    (when open?
      (react-dom/createPortal
       (r/as-element
        (into
         [:div.fixed.inset-0
          {:class "bg-black/40"
           #_#_:on-click on-close ; FIXME breaks combobox dropdown in modal (loadbuilder)
           :style {:z-index (z-index :modal-backdrop)
                   :pointer-events "auto"}}]
         content))
       js/document.body))))

(defn alert [& content]
  (let [[opts content] (opts+content content)]
    (into [modal-portal opts] content)))

(defn alert-content [& content]
  (let [[{:keys [full? class style] :as opts} content] (opts+content content)]
    (into [:div (merge (dissoc opts :full?)
                       {:class ["max-h-[85vh] overflow-auto data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none"
                                (when-not full? "p-[25px]")
                                (or class "w-[90vw] max-w-[500px]")]
                        :style (merge style {:z-index (z-index :alert-content)})})]
          content)))

(defn alert-description [& content]
  (into [:div {:class "text-mauve11 mt-4 mb-5 text-[15px] leading-normal"}]
        content))

(defn alert-title [& content]
  (into [:div {:class "text-mauve12 m-0 text-[17px] leading-normal font-bold"}]
        content))

; TODO maybe remove?
(defn alert-cancel [& content]
  (into [:<>] content))

(defn alert-action [& content]
  (into [:<>] content))

(defn loading-indicator [& content]
  [:div.text-xs.flex.items-center.gap-2
   (into [:<>] content)
   [:svg {:class "animate-spin h-5 w-5 text-indigo-700", :xmlns "http://www.w3.org/2000/svg", :fill "none", :viewBox "0 0 24 24"}
    [:circle {:class "opacity-25", :cx "12", :cy "12", :r "10", :stroke "currentColor", :stroke-width "4"}]
    [:path {:class "opacity-75", :fill "currentColor", :d "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"}]]])

; keep min+max values for frontend-only use
(defn mask-preset
  ([preset-kw-or-map] (mask-preset preset-kw-or-map @locale/!current-locale))
  ([preset-kw-or-map locale]
   (let [[thousands radix] (locale/number-separators locale)]
     (if (map? preset-kw-or-map)
       (merge {:thousandsSeparator thousands
               :radix radix}
              preset-kw-or-map)
       (get
        {:ui.mask/license-plate
         {:mask/prepare (fn [c] (-> (str c)
                                    (str/upper-case)
                                    (str/replace #"[^A-Z0-9\-ÜÖÄ]" "")
                                    (str/replace #"\-{2,}" "-")))
          :max-length 10
          :class "tabular-nums"}
         :ui.mask/ars-id
         {:mask/prepare (fn [c] (-> (str c)
                                    (str/upper-case)
                                    (str/replace #"[^A-Z0-9]" "")))
          :class "tabular-nums"}
         :ui.mask/truck-number
         {:mask/prepare (fn [c] (-> (str c)
                                    (str/upper-case)
                                    (str/replace #"[^A-Z0-9]" "")))
          :max-length 7
          :class "tabular-nums"}
         :ui.mask/vin
         {:mask/prepare (fn [c] (-> (str/upper-case (str c))
                                    (str/replace "I" "1")
                                    (str/replace "O" "0")
                                    (str/replace "Q" "0")
                                    (str/replace #"[^A-HJ-NPR-Z0-9]" "")))
          :max-length 17
          :class "tabular-nums"}
         :ui.mask/numeric-string
         {:mask/prepare (fn [c] (-> (str c)
                                    (str/replace #"[^0-9]" "")))
          :class "tabular-nums"}
         :ui.mask/int
         {:mask/prepare (fn [c] (-> (str c)
                                    (str/replace #"[^0-9]" "")))
          :mask/str->value (fn [s] (let [v (js/parseInt (str s))]
                                     (if (NaN? v) "" v)))
          :max-length 8
          :class "tabular-nums"}

         :ui.mask/factor
         {:mask/prepare (fn [c] (-> (str c)
                                    (str/replace #"[^0-9\.\,]" "")))
          :mask/value->str (fn [n] (locale/format-number {:minimumFractionDigits 2 :maximumFractionDigits 10} n))
          :mask/str->value (fn [s] (let [v (-> (str s)
                                               (str/replace thousands "")
                                               (str/replace radix ".")
                                               parse-double)]
                                     (if (NaN? v) "" v)))
          :max-length 10
          :class "tabular-nums"}

         ;; TODO migrate legacy imask stuff below to custom mask
         :ui.mask/percentage
         {:legacy? true
          :mask js/Number
          :scale 0
          :min 0
          :max 100
          :unmask :typed
          :class "tabular-nums"}
         :ui.mask/double
         {:legacy? true
          :mask js/Number
          :thousandsSeparator thousands
          :radix radix
          :blank-value ""
          :scale 2
          :min 0.0
          :max 999999.0
          :unmask :typed
          :class "tabular-nums"}
         :ui.mask/mileage
         {:legacy? true
          :mask js/Number
          :thousandsSeparator thousands
          :scale 0
          :min 0
          :max 9999999
          :unmask :typed
          :class "tabular-nums"}
         :ui.mask/hv-charge
         {:legacy? true
          :mask js/Number
          :thousandsSeparator thousands
          :radix radix
          :scale 2
          :min 0.0
          :max 999999.0
          :unmask :typed
          :class "tabular-nums"}
         :ui.mask/voltage-12v
         {:legacy? true
          :mask js/Number
          :normalizeZeros true
          :padFractionalZeros false ; avoids caret jumps when correcting just one fractional digit
          :radix radix
          :scale 2
          :min 0.0
          :max 99.99
          :unmask :typed
          :class "tabular-nums"}
         :ui.mask/time
         {:legacy? true
          :mask "00{:}00"
          :lazy false
          :overwrite true
          :class "font-mono"}
         :ui.mask/hu-au-valid-until
         {:legacy? true
          :mask "00.00"}
         :ui.mask/currency
         {:legacy? true
          :mask js/Number
          :thousandsSeparator thousands
          :radix radix
          :scale 2
          :min 0
          :max 999999999
          :unmask :currency-cents
          :padFractionalZeros false
          :class "tabular-nums"}}
        preset-kw-or-map)))))

(defn use-input-mask-legacy [{:keys [ref mask value on-change on-input min max class] :as props}]
  (let [mask (merge {:min min :max max}
                    (mask-preset mask))
        !initially-rendered? (hooks/use-ref false)
        on-change-or-input (or on-change on-input identity)
        on-accept (fn [_value-str imask _e]
                    (if (str/blank? (.-value imask))
                      (on-change-or-input #js {:target #js {:value (:blank-value mask)}}) ; TODO ugh, masked inputs can't emit real change events. return plain value and remove (.. -target -value) wherever input is used
                      (let [v (case (:unmask mask)
                                true (.-unmaskedValue imask)
                                :currency-cents (Math/round (* 100 (js/parseFloat (.-unmaskedValue imask))))
                                :typed (js/parseFloat (.-unmaskedValue imask))
                                (.-value imask))]
                        (on-change-or-input #js {:target #js {:value v}}))))
        ^js p (imask/useIMask (clj->js mask)
                              (if ref
                                #js {:onAccept on-accept :ref ref}
                                #js {:onAccept on-accept}))]
    (hooks/use-effect
     (fn []
       (when (and (.-maskRef p) (not @!initially-rendered?))
         (cond
           (= 0 value)
           (.setValue p "0") ; because setTypedValue ignores 0

           (and (number? value) (= :currency-cents (:unmask mask)))
           (.setTypedValue p (/ value 100.0))

           (number? value)
           (.setTypedValue p value)

           :else ; TODO fix clearing numbers, clear should reset value to nil, not 0 (km, kWh, Volt)
           (.setValue p (str value)))

         (reset! !initially-rendered? true)))
     [(.-maskRef p) value @!initially-rendered?])

    (-> props
        (merge (select-keys mask [:right :left :min :max]))
        (dissoc :mask :on-change :on-input :value)
        (assoc
         :class (class-vec class (:class mask))
         :ref (.-ref p)
         :type :text))))

(defn use-input-mask [{:as opts :keys [mask class on-change on-input value]}]
  (let [{:as mask :keys [legacy?] :mask/keys [prepare str->value value->str]} (mask-preset mask)
        legacy? (or legacy? (map? (:mask opts)))]
    (if legacy?
      (use-input-mask-legacy opts)
      (let [!ui-value (hooks/use-state nil)
            on-input (or on-change on-input)
            value->str (or value->str str)
            str->value (or str->value identity)]
        (-> (dissoc opts :prepare :mask :on-input :on-change :value)
            (merge (filter simple-ident? mask)
                   {:type :text
                    :class (class-vec class (:class mask))
                    :value (or @!ui-value (value->str value))
                    :on-blur (fn [^js e]
                               (reset! !ui-value nil)
                               (on-input #js {:target #js {:value (str->value (prepare (.. e -target -value)))}}))
                    :on-change (fn [^js e] ; TODO check keydown isComposing to allow dead keys
                                 (reset! !ui-value (prepare (.. e -target -value)))
                                 (on-input #js {:target #js {:value (str->value (prepare (.. e -target -value)))}}))}))))))

(defn clear-input [{:keys [size on-click]}]
  (let [size (case (or size 2)
               1 9
               2 10
               3 11
               4 12)]
    [:div.group.rounded.flex.justify-center.items-center.cursor-pointer
     {:on-click (fn [^js e] (.stopPropagation e) (on-click e))
      :class "hover:bg-slate-200/80"
      :style {:width (+ 15 size) :height (+ 15 size)}}
     [:div.rounded-full.flex.items-center.justify-center.group-hover:bg-red-600.group-hover:text-white.print:hidden.text-slate-700.bg-slate-200
      {:style {:width (+ 4 size) :height (+ 4 size)}}
      [icon {:width size :height size} :cross-2]]]))

(defc input [opts]
  (let [{:as opts :keys [component variant value on-change on-clear mask left right override-class width class style size disabled? children]} (with-events opts)
        size (or size 2)
        font-size-class (get {1 "text-[12px]"
                              2 "text-xs"
                              3 "text-sm"
                              4 "text-base"} size)
        {:as opts :keys [left right class]}
        (cond->
          (merge opts
                 {:disabled (:disabled? opts)
                  :style (merge (when (= :textarea component) {:form-sizing "normal"}) style)
                  :class (class-vec "leading-none outline-none px-2 bg-transparent flex-auto w-full appearance-none resize-none"
                                    font-size-class
                                    (if (= :textarea component)
                                      "py-2"
                                      (get {1 "h-[28px]"
                                            2 "h-[32px]"
                                            3 "h-[38px]"
                                            4 "h-[42px]"} size))
                                    (:class mask)
                                    class
                                    (if disabled?
                                      "cursor-not-allowed placeholder:text-slate-300"
                                      "placeholder:text-slate-400"))})
          mask use-input-mask)
        input-opts (dissoc opts :component :on-clear :override-class :left :right :size :variant :disabled?)]
    [:div
     {:on-click (fn [^js e] (.. e -currentTarget (querySelector "input,textarea,select") (focus)))
      :class (class-vec "flex leading-none relative"
                        (if (= :mobile variant)
                          ["bg-transparent pl-2" (when-not right "pr-4")]
                          [(if disabled?
                             "border-slate-100"
                             "bg-white hover:bg-slate-50 hover:border-slate-300")
                           "border border-slate-200 rounded has-[input:focus-within]:ring-2 has-[textarea:focus-within]:ring-2 has-[select:focus-within]:ring-2 ring-indigo-500 ring-offset-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.04)]"])
                        (if disabled?
                          "cursor-not-allowed bg-slate-100/60 text-slate-500"
                          "cursor-text")
                        (case width
                          :xs "w-14" ; 2 characters
                          :sm "w-20" ; 4 to 5 characters
                          :md "w-40" ; 8 to 12 characters
                          :lg "w-56"
                          :xl "w-96"
                          "w-full")
                        "print:shadow-none"
                        override-class)}

     (when left
       [:div.text-slate-400.select-none
        {:class (class-vec font-size-class (when (string? left) "pointer-events-none"))}
        left])
     (into [(or component :input) input-opts] children)
     (let [clearable? (and on-clear (not (str/blank? value)))]
       (when (or right clearable?)
         [:div.flex.items-center.gap-2 {:class (when-not (= :select component) "mr-2")}
          (when right
            [:div.text-slate-400.select-none
             {:class (class-vec font-size-class (when (string? right) "pointer-events-none"))}
             right])
          (when clearable?
            [clear-input {:on-click on-clear :size size}])]))]))

(defscene masked-input ; FIXME typing is very slow
  :params (r/atom {})
  [!state]
  [:div.flex.gap-10
   [:div {:class "w-1/2"}
    [:div.my-4.flex.flex-col.gap-2
     [:div.my-2
      [input {:mask :ui.mask/int
              :on-clear #(swap! !state dissoc :int)
              :value (:int @!state)
              :on-change #(swap! !state assoc :int (.. % -target -value))
              :right "int"
              :placeholder (tr :vehicle.status/battery-charge)}]]]

    [:div.my-4.flex.flex-col.gap-2
     [input {:width :lg
             :placeholder "Schinkestraße 9 (size :lg)"
             :value (:address/street @!state)
             :on-change #(swap! !state assoc :address/street (.. % -target -value))
             :class "tabular-nums"}]
     [:div.flex.gap-2
      [input {:width :xs
              :placeholder "12047"
              :mask :ui.mask/numeric-string
              :value (:address/postcode @!state)
              :on-change #(swap! !state assoc :address/postcode (.. % -target -value))
              :class "tabular-nums"}]
      [input {:width :md
              :placeholder "Berlin (size :xs + :md)"
              :value (:address/city @!state)
              :on-change #(swap! !state assoc :address/city (.. % -target -value))}]]]


    [:div.my-4
     [input {:placeholder "Description (:component :textarea)"
             :rows 5
             :component :textarea
             :value (:description @!state)
             :on-change #(swap! !state assoc :description (.. % -target -value))}]]


    [:div.my-2
     [input {:mask :ui.mask/hu-au-valid-until
             :on-clear #(swap! !state dissoc :valid-until)
             :value (:valid-until @!state)
             :on-change #(swap! !state assoc :valid-until (.. % -target -value))
             :placeholder (tr "MM.YY")
             :class "tabular-nums"}]]

    [:div.my-2
     [input {:mask :ui.mask/currency
             :right "€"
             :on-clear #(swap! !state dissoc :price)
             :value (:price @!state)
             :on-change #(swap! !state assoc :price (.. % -target -value))
             :placeholder (tr "Price")}]]

    [:div.my-2
     [input {:mask :ui.mask/license-plate
             :on-clear #(swap! !state dissoc :license-plate)
             :value (:license-plate @!state)
             :on-change #(swap! !state assoc :license-plate (.. % -target -value))
             :placeholder (tr "License plate")}]]

    [:div.my-2
     [input {:mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :mileage)
             :value (:mileage @!state)
             :on-change #(swap! !state assoc :mileage (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]]

    [:div.my-2
     [input {:mask :ui.mask/hv-charge
             :on-clear #(swap! !state dissoc :charge)
             :value (:charge @!state)
             :on-change #(swap! !state assoc :charge (.. % -target -value))
             :right "kWh"
             :placeholder (tr "Current charge")}]]

    [:div.my-2
     [input {:mask :ui.mask/voltage-12v
             :on-clear #(swap! !state dissoc :voltage-12v)
             :value (:voltage-12v @!state)
             :on-change #(swap! !state assoc :voltage-12v (.. % -target -value))
             :right "Volt"
             :placeholder (tr :vehicle.status/battery-charge)}]]

    [:div.my-2
     [input {:mask :ui.mask/vin
             :on-clear #(swap! !state dissoc :vin)
             :value (:vin @!state)
             :on-change #(swap! !state assoc :vin (.. % -target -value))
             :placeholder (tr :vehicle/vin)}]]

    [:div.my-2
     [input {:mask :ui.mask/time
             :on-clear #(swap! !state dissoc :time)
             :value (:time @!state)
             :on-change #(swap! !state assoc :time (.. % -target -value))
             :placeholder (tr "Time")}]]
    [:div.my-2
     [input {:mask :ui.mask/hu-au-valid-until
             :on-clear #(swap! !state dissoc :valid-until)
             :value (:valid-until @!state)
             :on-change #(swap! !state assoc :valid-until (.. % -target -value))
             :placeholder "disabled"
             :disabled? true}]]

    [:div.my-2
     [input {:mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :mileage)
             :value (:mileage @!state)
             :on-change #(swap! !state assoc :mileage (.. % -target -value))
             :right "km"
             :placeholder "Mileage"}]]

    [:div.my-2.bg-slate-100.p-4.rounded
     [:div.my-2
      [input {:mask :ui.mask/license-plate
              :on-clear #(swap! !state dissoc :license-plate)
              :value (:license-plate @!state)
              :on-change #(swap! !state assoc :license-plate (.. % -target -value))
              :placeholder (tr "License plate")}]]

     [:div.my-2
      [input {:mask :ui.mask/mileage
              :on-clear #(swap! !state dissoc :mileage)
              :value (:mileage @!state)
              :on-change #(swap! !state assoc :mileage (.. % -target -value))
              :right "km"
              :placeholder (tr "Mileage")}]]

     [:div.my-2
      [input {:mask :ui.mask/hu-au-valid-until
              :on-clear #(swap! !state dissoc :valid-until)
              :value (:valid-until @!state)
              :on-change #(swap! !state assoc :valid-until (.. % -target -value))
              :placeholder "disabled"
              :disabled? true}]]
     [:div.my-2
      [input {:mask :ui.mask/mileage
              :on-clear #(swap! !state dissoc :mileage)
              :value (:mileage @!state)
              :on-change #(swap! !state assoc :mileage (.. % -target -value))
              :right "km"
              :placeholder "disabled Mileage"
              :disabled? true}]]]
    [:div.my-2
     [input {:width :xs
             :mask :ui.mask/license-plate
             :on-clear #(swap! !state dissoc :license-plate)
             :value (:license-plate @!state)
             :on-change #(swap! !state assoc :license-plate (.. % -target -value))
             :placeholder (tr "License plate")}]]
    [:div.my-2
     [input {:width :xs
             :mask :ui.mask/time
             :value (:time @!state)
             :on-change #(swap! !state assoc :time (.. % -target -value))
             :placeholder (tr "Time")}]]
    [:div.my-2
     [input {:width :sm
             :mask :ui.mask/hu-au-valid-until
             :on-clear #(swap! !state dissoc :valid-until)
             :value (:valid-until @!state)
             :on-change #(swap! !state assoc :valid-until (.. % -target -value))
             :placeholder (tr "MM.YY")
             :class "tabular-nums"}]]
    [:div.my-2
     [input {:width :md
             :mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :test)
             :value (:test @!state)
             :on-change #(swap! !state assoc :test (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]]
    [:div.my-2
     [input {:width :md
             :mask :ui.mask/hv-charge
             :on-clear #(swap! !state dissoc :charge)
             :value (:charge @!state)
             :on-change #(swap! !state assoc :charge (.. % -target -value))
             :right "kWh"
             :placeholder (tr "Current charge")}]]
    [:div.my-2
     [input {:width :lg
             :mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :test)
             :value (:test @!state)
             :on-change #(swap! !state assoc :test (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]]

    [:div.my-2.flex.gap-2
     [input {:size 1
             :mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :mileage)
             :value (:mileage @!state)
             :on-change #(swap! !state assoc :mileage (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]
     [button {:size 1} "Absenden"]]

    [:div.my-2.flex.gap-2
     [input {:size 2
             :mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :mileage)
             :value (:mileage @!state)
             :on-change #(swap! !state assoc :mileage (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]
     [button {:size 2} "Absenden"]]
    [:div.my-2.flex.gap-2
     [input {:size 3
             :mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :mileage)
             :value (:mileage @!state)
             :on-change #(swap! !state assoc :mileage (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]
     [button {:size 3} "Absenden"]]
    [:div.my-2.flex.gap-2
     [input {:size 4
             :mask :ui.mask/mileage
             :on-clear #(swap! !state dissoc :mileage)
             :value (:mileage @!state)
             :on-change #(swap! !state assoc :mileage (.. % -target -value))
             :right "km"
             :placeholder (tr "Mileage")}]
     [button {:size 4} "Absenden"]]]

   [:div {:class "w-1/2"}
    [:div.font-mono.text-xs (prn-str @!state)]]])

(defc select [props]
  (let [{:as props :keys [value on-change choices placeholder left right size disabled? placeholder-selectable? class style]} (with-events props)
        size (or size 2)
        selected-choice (first (filter #(= (:ui/value %) value) choices))
        select-props (dissoc props :value :on-change :choices :right)]
    [input (merge select-props
                  {:component :select
                   :value (if (some? selected-choice)
                            (pr-str (:ui/value selected-choice)) "")
                   :on-change (fn [^js e]
                                (on-change (edn/read-string (.. e -target -value)))
                                (.blur (.-target e)))
                   :class (class-vec (when-not disabled? "cursor-pointer")
                                     (when (nil? value) "text-slate-400")
                                     "pr-5" ; make space below absolutely positioned chevron
                                     class)
                   :override-class (when-not disabled? "cursor-pointer")
                   :style (merge {:line-height 1.4} style)
                   :children (into [(when placeholder
                                      [:option (cond-> {:value ""}
                                                 (not placeholder-selectable?) (assoc :disabled true
                                                                                      :hidden true)) placeholder])]
                                   (map (fn [{:as option :ui/keys [value label]}]
                                          [:option (merge
                                                    (dissoc option :ui/value :ui/label)
                                                    {:value (pr-str value)}) label]))
                                   choices)
                   :left (when left [:div.absolute.pointer-events-none
                                     {:class (get {1 "top-[7px]"
                                                   2 "top-[10px]"
                                                   3 "top-[12px]"
                                                   4 "top-[15px]"} size)}
                                     left])
                   :right [:div.flex.items-center.gap-2.absolute.pointer-events-none.right-2
                           {:class (get {1 "top-[7px]"
                                         2 "top-[10px]"
                                         3 "top-[12px]"
                                         4 "top-[15px]"} size)}
                           (:right props)
                           [:div.text-slate-500.cursor-pointer.hover:text-slate-700
                            [icon {:size 14} :chevron-down]]]})]))

(defn with-element!
  "Finds an element and calls callback with it. Retries with requestAnimationFrame if not found."
  ([query-selector-or-el callback] (with-element! query-selector-or-el callback 20))
  ([query-selector-or-el callback retries]
   (if (pos? retries)
     (if-let [^js el (if (string? query-selector-or-el) (js/document.querySelector query-selector-or-el) query-selector-or-el)]
       (callback el)
       (js/requestAnimationFrame #(with-element! query-selector-or-el callback (dec retries))))
     (js/console.warn "with-element! did not find" query-selector-or-el))))

(defn scroll-into-view!
  ([query-selector-or-el] (scroll-into-view! query-selector-or-el nil))
  ([query-selector-or-el options]
   (with-element! query-selector-or-el #(.scrollIntoView ^js % (clj->js options)))))

(defn scroll-into-view-with-margin!
  "Scrolls element into view, accounting for fixed headers/footers.
   If element is taller than visible area, aligns top of element with top margin."
  ([query-selector-or-el] (scroll-into-view-with-margin! query-selector-or-el {}))
  ([query-selector-or-el {:keys [top-margin bottom-margin smooth?] :or {top-margin 170 bottom-margin 350}}] ; to ensure combobox options are visible
   (with-element! query-selector-or-el
     (fn [^js el]
       (let [rect (.getBoundingClientRect el)
             viewport-height js/window.innerHeight
             el-top (.-top rect)
             el-bottom (.-bottom rect)
             el-height (- el-bottom el-top)
             visible-height (- viewport-height top-margin bottom-margin)
             visible-top top-margin
             visible-bottom (- viewport-height bottom-margin)
             behavior (if smooth? "smooth" "instant")]
         (cond
           ;; Element is taller than viewport - align top with top margin
           (> el-height visible-height)
           (.scrollBy js/window #js {:top (- el-top visible-top) :behavior behavior})

           ;; Element is above the visible area
           (< el-top visible-top)
           (.scrollBy js/window #js {:top (- el-top visible-top) :behavior behavior})

           ;; Element is below the visible area
           (> el-bottom visible-bottom)
           (.scrollBy js/window #js {:top (- el-bottom visible-bottom) :behavior behavior})))))))

(defn focus!
  "Focuses an element, walking up to find nearest focusable parent if needed."
  ([query-selector-or-el] (focus! query-selector-or-el nil))
  ([query-selector-or-el {:keys [find-focusable-parent?]}]
   (with-element! query-selector-or-el
     (fn [el]
       (if find-focusable-parent?
         (loop [current el]
           (when current
             (if (= "0" (.getAttribute current "tabindex"))
               (.focus current)
               (recur (.-parentElement current)))))
         (.focus el))))))

(defn locations-autocomplete-path
  ([k]
   (locations-autocomplete-path k []))
  ([k path]
   (vec (concat [:current-route/transient ::locations-autocomplete k] path))))

(defn !locations-autocomplete-state
  ([k]
   (!locations-autocomplete-state k []))
  ([k path]
   (!locations-autocomplete-state k path nil))
  ([k path not-found]
   (deframe/!get-in (locations-autocomplete-path k path) not-found)))

(defn locations-autocomplete-reset!
  "Resets the autocomplete state to keep only :button-el (used on close)."
  [component-key]
  (deframe/assoc-in! (locations-autocomplete-path component-key)
                     (select-keys @(!locations-autocomplete-state component-key)
                                  [:button-el])))

(defn locations-autocomplete-item [{:as opts :keys [component-key]} item]
  (let [!item-el (hooks/use-ref nil)
        selected-item @(!locations-autocomplete-state component-key [:selected-item])
        button-el @(!locations-autocomplete-state component-key [:button-el])
        selected? (= item selected-item)
        close! (fn [event]
                 ((:on-select-item opts) item event)
                 (locations-autocomplete-reset! component-key)
                 (when button-el (.focus button-el)))]
    (hooks/use-effect (fn [] (when-let [el @!item-el]
                               (when selected?
                                 (scrollIntoView el #js {:scrollMode "if-needed"
                                                         :block "nearest"
                                                         :boundary (fn [parent]
                                                                     (not (or (identical? parent js/document.body)
                                                                              (identical? parent js/document.documentElement))))}))))
                      [selected-item])
    [:li.text-xs.border-b.last:border-b-0.px-4.py-2.cursor-pointer.group
     {:data-selected selected?
      :class (if selected?
               ["bg-indigo-600" "hover:bg-indigo-700" "text-white"]
               ["hover:bg-indigo-200"])
      :ref !item-el
      :on-click close!}
     [(:item-view opts) item]]))

(defn locations-autocomplete-items [{:as opts :keys [component-key]}]
  (let [!items-el (hooks/use-ref nil)
        !input-el (hooks/use-ref nil)
        filtered-items @(!locations-autocomplete-state component-key [:filtered-items])
        filtered-items-results (get-in filtered-items [:result :results])
        loading? @(!locations-autocomplete-state component-key [:loading?])
        error @(!locations-autocomplete-state component-key [:error])
        selected-item @(!locations-autocomplete-state component-key [:selected-item])
        button-el @(!locations-autocomplete-state component-key [:button-el])
        on-change (hooks/use-callback (gf/debounce
                                       (fn [event]
                                         (deframe/assoc-in! (locations-autocomplete-path component-key [:search-text]) (.. event -target -value)))
                                       200))]
    (hooks/use-effect
     (fn []
       (let [on-click (fn [event]
                        (let [target (.-target event)]
                          (when (and button-el
                                     (not= button-el target)
                                     (not (.contains button-el target))
                                     (not= @!items-el target)
                                     (not (.contains @!items-el target)))
                            (locations-autocomplete-reset! component-key))))]
         (when @!items-el
           (let [popper-instance (popper/createPopper button-el @!items-el (clj->js {:placement :bottom-start
                                                                                     :modifiers [{:name :flip}]}))]
             (js/document.addEventListener "click" on-click)
             (fn []
               (.destroy popper-instance)
               (js/document.removeEventListener "click" on-click))))))
     [button-el])
    (hooks/use-effect
     (fn []
       (when-let [el @!input-el]
         (.focus el #js {:preventScroll true}))
       js/undefined)
     [])
    (hooks/use-effect
     #(when (seq filtered-items-results)
        (deframe/assoc-in! (locations-autocomplete-path component-key [:selected-item]) (first filtered-items-results)))
     [filtered-items-results])
    (hooks/use-effect
     (fn []
       (let [filter-result ((:filter opts) @(!locations-autocomplete-state component-key))
             search-text @(!locations-autocomplete-state component-key [:search-text])]
         #_@(!locations-autocomplete-state "WBA11EE0005Z66405" [:search-text])
         (when-not (.-then filter-result)
           (deframe/assoc-in! (locations-autocomplete-path component-key [:filtered-items :result :results]) filter-result))))
     [@(!locations-autocomplete-state component-key [:search-text])
      (true? @(!locations-autocomplete-state component-key [:open?]))])
    [:div.rounded-lg.bg-white.border.shadow.w-96.absolute.left-0.top-0
     {:ref !items-el :class (when-not button-el "opacity-0")
      :style {:z-index (z-index :combobox-choices)}}
     [:div.w-full.px-2.pt-2.border-b.shadow
      [input {:ref !input-el
              :variant :flex
              :placeholder (:input-placeholder opts)
              :data-testid "locations-autocomplete-input"
              :on-key-down (fn [e]
                             (let [items-count (count filtered-items-results)
                                   i (util/index-of selected-item filtered-items-results)]
                               (cond
                                 (= "Escape" (.-key e))
                                 (do (locations-autocomplete-reset! component-key)
                                     (when button-el (.focus button-el)))
                                 (= "Enter" (.-key e))
                                 (do
                                   ((:on-select-item opts) selected-item e)
                                   (locations-autocomplete-reset! component-key)
                                   (when button-el (.focus button-el)))
                                 (= "ArrowDown" (.-key e))
                                 (deframe/assoc-in!
                                  (locations-autocomplete-path component-key [:selected-item])
                                  (nth filtered-items-results (mod (inc i) items-count)))
                                 (= "ArrowUp" (.-key e))
                                 (deframe/assoc-in!
                                  (locations-autocomplete-path component-key [:selected-item])
                                  (nth filtered-items-results (mod (dec i) items-count))))))
              :on-focus #(deframe/assoc-in! (locations-autocomplete-path component-key [:open?]) true)
              :on-change on-change}]

      [:label.py-1.block.flex.items-center.text-slate9.text-xs.font-medium
       [:input {:checked @(!locations-autocomplete-state component-key [:search-by-dealer-code?])
                :type "checkbox"
                :class "h-3 w-3 mr-1 focus:ring-indigo-500 border-gray-300 rounded"
                :on-change #(deframe/assoc-in!
                             (locations-autocomplete-path component-key [:search-by-dealer-code?])
                             (not @(!locations-autocomplete-state component-key [:search-by-dealer-code?])))}]
       (tr "search by dealer code")]]
     (cond (:error filtered-items)
           [:div.p-2.text-red-400 (:error filtered-items)]

           (:loading? filtered-items)
           [:div.flex.flex-col.items-center.justify-center.px-4.py-3
            [spinner {:size 24}]
            [:span.text-slate8.text-sm.font-medium
             (str (tr "Fetching things") "…")]]

           (:result filtered-items)
           (into [:ul.overflow-y-auto {:data-testid "locations-autocomplete-list" :style {:max-height 300}}]
                 (map (fn [item]
                        [locations-autocomplete-item opts item]))
                 filtered-items-results))]))

#_(deframe/!route-get-in [::locations-autocomplete])

(defn locations-autocomplete [& content]
  (let [[{:as opts :keys [value component-key data-testid readonly? overridden? on-clear] :ui/keys [style]} content] (opts+content content)
        open? @(!locations-autocomplete-state component-key [:open?])
        on-click (when-not readonly?
                   (fn [event]
                     (.preventDefault event)
                     (if open?
                       (locations-autocomplete-reset! component-key)
                       (deframe/assoc-in!
                        (locations-autocomplete-path component-key [:open?])
                        true))))]
    [:div.relative.flex-auto
     [:div
      {:data-testid data-testid
       :tab-index 0
       :on-key-down (when on-click (fn [^js e]
                                     (when (contains? #{"Enter" "Space"} (.-code e))
                                       (.preventDefault e)
                                       (on-click e))))
       :class (case style
                :style/mobile ["bg-white" "px-4" "py-2" "hover:bg-slate-50" "focus-visible:bg-indigo-50" "focus:outline-none"]
                [(if (:disabled? opts)
                   "border-slate-100"
                   "bg-white hover:border-slate-300 cursor-pointer ")
                 "border border-slate-200 rounded has-[input:focus-within]:ring-2 has-[textarea:focus-within]:ring-2 ring-indigo-500 ring-offset-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.04)]"])
       :ref (fn [el]
              (when el
                (deframe/assoc-in! (locations-autocomplete-path component-key [:button-el]) el)))
       :size :unsized
       :on-click on-click}
      [:div.flex.justify-stretch.items-center
       {:class (case style
                 :style/mobile ["text-base"]
                 ["text-xs" "px-2" "py-1"])}
       (cond
         value [:div.flex-grow
                {:class [(when readonly? "opacity-50")
                         (when overridden? "line-through")]}
                [(:selected-item-view opts) value]]
         readonly? [:span.text-slate-500 "-"]
         :else (:button-placeholder opts))

       (when (and on-clear value (not readonly?)) [:div.flex-shrink [clear-input {:on-click on-clear}]])]]
     (when open?
       [slot {:container "floating-components"}
        [locations-autocomplete-items opts]])]))

(defn commands-button []
  [tooltip
   {:content (tr "Quick navigation")
    :shortcut [:cmd "k"]}
   [:button.h-6.w-6.rounded-full.hover:bg-indigo-100.flex.items-center.justify-center.flex-shrink-0.focus-visible:ring-2.ring-indigo-500.ring-offset-2
    {:tab-index -1
     :on-click #(deframe/assoc! :ductile.ui.commands/open? true)}
    [icon :magnifying-glass]]])

(def mobile-preview-popup-opts
  {:popup true
   :width 360
   :height 512})

(defn open-popup! [opts href]
  (js/open href (str "Mobile Preview for: " href) (str/join "," (map (fn [[k v]] (str (name k) "=" v)) opts))))

(defn with-mobile-preview [{:as attrs :keys [href]}]
  (assoc attrs
         :target "_blank"
         :on-click (fn [event]
                     (when-not (.-metaKey event)
                       (.preventDefault event)
                       (open-popup! mobile-preview-popup-opts href)))))

(defn nav-bar-item-active? [path-params template]
  (= (router/path template path-params) (.. js/window -location -pathname)))

(defc nav-bar-item [path-params item]
  (with-scope-protected-value item
    (let [{:keys [label template items]} item
          active? (if items
                    (some #(nav-bar-item-active? path-params (:template %)) items)
                    (nav-bar-item-active? path-params template))]
      [:li.nav-bar-item
       {:class (str "transition-all md:hover:bg-indigo-50 hover:text-indigo-600 md:flex md:border-b-[3px] "
                    (if active?
                      "border-indigo-600 hover:border-indigo-600 text-indigo-600 "
                      "border-transparent ")
                    (when items "group"))
        :on-mouse-enter (fn [e]
                          (let [x (.. e -target getBoundingClientRect -x)
                                menu (.. e -target (closest ".nav-bar-item") (querySelector ".nav-bar-item-menu"))
                                scroll-x (.. e -target (closest "nav") -scrollLeft)]
                            (j/assoc-in! menu [:style :marginLeft] (str (- scroll-x) "px"))))}
       (if items
         [:<>
          [:a.px-2.pb-1.pt-3.md:pb-0.md:pt-0.inline-flex.items-center.cursor-default.active:text-indigo-900.active:bg-indigo-100.focus-visible:bg-indigo-100.text-nowrap
           {:tab-index -1
            :class (str "md:m-[2px] relative "
                        (when active? "font-bold"))
            :href ""}
           [:span.inline-flex.items-center.gap-1 {:class "mt-0.5"}
            label
            [icon {:size 12 :class "hidden flex-shrink-0 md:block relative w-[12px]"} :chevron-down]]]
          [:div.nav-bar-item-menu.md:hidden.group-hover:block.md:absolute.pb-2.md:p-0.border-b.md:border-b-0.group-focus-within:block
           {:class "md:top-[38px] md:pt-[6px] md:-ml-1 z-[50]"}
           (into [:ul.flex.flex-wrap.md:block.md:rounded-md.md:bg-white.md:shadow-lg.md:border.md:border-slate-300.md:overflow-hidden]
                 (map (fn [item]
                        (with-scope-protected-value item
                          (let [{:keys [label description template mobile? query-string]} item]
                            [:li.md:border-b.last:border-b-0.text-black
                             [:a.block.px-3.mx-1.md:mx-0.md:px-4.py-2.text-xs.hover:bg-indigo-50.rounded.md:rounded-none.focus-visible:bg-indigo-100
                              (cond-> {:tab-index -1
                                       :href (router/path template path-params query-string)}
                                (nav-bar-item-active? path-params template) (assoc :class "font-bold text-indigo-600 border-indigo-400 border-l-4")
                                mobile? with-mobile-preview)
                              label
                              (when description
                                [:div.text-slate-500.font-normal
                                 {:class "text-[11px] md:max-w-[270px]"}
                                 description])]]))))
                 items)]]
         [:a.flex.items-center.px-2.py-3.md:py-0.border-b.md:border-b-0.hover:bg-indigo-50.md:hover:bg-transparent.active:text-indigo-900.active:bg-indigo-100.focus-visible:bg-indigo-100
          {:tab-index -1
           :href (router/path template path-params)}
          [:span {:class (class-vec "mt-0.5" (when active? "font-bold border-b-[2px] border-indigo-600 md:border-b-0"))} label]])])))

(defc nav-bar []
  (r/with-let [!open? (r/atom false)]
    (when-let [current-user @deframe/!current-user]
      (let [path-params {:compound-name @deframe/!current-compound
                         :compound/name @deframe/!current-compound ;; allow for slow migration away from :compound-name in routes
                         :user current-user}
            nav (router/build-nav path-params)
            nav-items (map (partial nav-bar-item path-params) nav)]
        [:<>
         #_[:div.h-12.md:hidden.print:hidden]
         (when @!open?
           [:div.fixed.left-0.top-0.w-screen.h-screen.bg-black.bg-opacity-70.z-10.print:hidden
            {:on-click #(reset! !open? false)}])
         [env-bar]
         [:div.overflow-x-auto.bg-white.px-5.flex.items-center.w-full.justify-between.left-0.top-0.z-30.print:hidden.border-b.border-gray-300
          {:style {:scrollbar-width :none}}
          [:div.flex.items-center.shrink-0
           [:a.mr-3.py-3 {:class "-ml-0.5" :href "/" :tab-index -1}
            [:img {:src (maybe-add-host "/images/logo.svg") :width 57}]]
           (when (< 1 (count (:user/compounds @deframe/!current-user)))
             [:div.pr-2
              [compound-select @deframe/!current-user]])

           [:div.md:hidden
            [:button.text-sm
             {:on-click #(swap! !open? not)}
             [:span.inline-flex.items-center
              [:a {:class "mr-[2px]"}
               (or (->> nav
                        (filter #(nav-bar-item-active? path-params (:template %)))
                        first
                        :label)
                   (tr "Menu"))]
              [icon {:size 12} :chevron-down]]]
            (when @!open?
              [:nav.absolute.left-6.right-6.z-20
               {:class "top-[55px]"}
               (into [:ul.border.shadow-xl.bg-white.rounded-lg] nav-items)])]]

          [:nav.hidden.md:block.self-stretch.overflow-x-auto.w-full.scroll-shadows-x {:style {:scrollbar-width :none}}
           (into [:ul.flex.h-full {:class "text-[12px]"}] nav-items)]

          [:div.flex.items-center.gap-3.self-stretch
           [commands-button]
           [debug-mode-indicator]
           [environment-indicator]
           [user-dropdown @deframe/!current-user]]]]))))

(defn slot [{:keys [container]} & content]
  (react-dom/createPortal
   (r/as-element
    (into [:<>] content))
   (js/document.getElementById (name container))))

(defn speculative-transactions-indicator [{:keys [use-case]}]
  [:div.flex.items-center.px-2.text-white.gap-2.justify-between
   {:style {:height 22 :background-color "#3e9b4f" :font-size 11}}
   [:span.flex.items-center.gap-1
    [icon {:size 12} :share-1]
    (if use-case
      [:<>
       [:span "Speculative transactions enabled for:"]
       [:span.font-bold use-case]]
      [:span "Speculative transactions enabled"])]
   [:button.flex.items-center.gap-1.rounded-sm.leading-0
    {:on-click (fn [event]
                 (.stopPropagation event)
                 (.preventDefault event)
                 (http/$disable-speculative-transactions!))
     :style {:padding "0px 4px" :font-size "10px"} :class "bg-[#2a7e3b] hover:bg-[#203c25]"}
    [icon {:size 10} :cross-2]
    [:span.font-bold "Disable"]]])

(defn page [& content]
  (into [:div.min-h-screen.bg-gray-200.relative.print:border-0.print:bg-white] content))

(defn page-header [& content]
  (let [title (first content)
        desc :div.flex.bg-white.border-b.justify-between.py-3.px-5.border-b.shadow-md.items-center]
    (if (string? title)
      [desc [:h1.font-bold.text-lg title]]
      (into [desc] content))))

(defscene page-header-card
  "A higher-order component that adds a sticky header to the top of the
   current page. If the first argument is a string, it will render it as
   a title otherwise treats arguments as opts & arbitrary hiccup content."
  [page-header "Sondertransporte"])


(defn data-table
  "A higher-order component that takes arbitrary content to be rendered in a `:table`.
   By default, wraps everything in a vertically scrollable container so as to not overflow the page."
  [& content]
  (let [{:as options :keys [variant]} (first content)]
    [(keyword (str "div.data-table" (when (and (map? options) variant)
                                      (str "-" (name variant)))))
     (into [:table] content)]))

(defscene data-table-card
  [data-table
   [:thead [:tr
            [:th.text-right "Mandant"]
            [:th.text-right "Firma"]
            [:th "Transport ID"]
            [:th "VIN"]
            [:th "Eingangsdatum"]]]
   (into [:tbody]
         (map (fn [n]
                [:tr
                 [:td.text-right (str "000" n)]
                 [:td.text-right "012"]
                 [:td (gensym)]
                 [:td (random-uuid)]
                 [:td "03.12.2020, 13:55:39"]])
              (range 40)))])

(defn render-viewer [v]
  (let [{:as viewer :keys [transform-fn render-fn]}
        (->> ductile.clerk.viewer/ductile-viewers
             (remove #(= (:name %) 'nextjournal.clerk.viewer/table-viewer))
             (filter #((:pred %) v))
             first)]
    (cond
      viewer (let [presented-value
                   (cond->> v
                     (and transform-fn (not= transform-fn viewer/mark-presented)) (transform-fn {:nextjournal/value v}))]
               (if render-fn
                 ((eval render-fn) presented-value)
                 (:nextjournal/value presented-value)))
      (= :nextjournal/missing v) ""
      (boolean? v) (str v)
      :else v)))

(defn code39
  ([data]
   (code39 {} data))
  ([opts data]
   [:img (assoc opts :src (str "/barcode/code39/" (js/encodeURIComponent data)))]))

(defn qr-code
  ([data]
   (qr-code {:size 150} data))
  ([{:as opts :keys [size width]} data]
   (let [opts (-> opts (dissoc :size) (assoc :width (or size width)) (assoc :height (or size width)))]
     [:img.flex-shrink-0 (assoc opts :src (str "/barcode/qr/" (base64u/encode data)))])))

(defn table-cell [& content]
  (let [[opts content] (opts+content content)]
    (into [:td
           {:class (concat ["px-2" "py-1" "text-[12px]" "whitespace-nowrap"]
                           (:extra-classes opts))}]
          content)))

(defn table-row [& content]
  (into [:tr.even:bg-slate-50.hover:bg-indigo-50.border-b.group] content))

(defn table-header-cell [& content]
  (let [[opts content] (opts+content content)]
    (into [:th
           {:class (concat ["text-slate-600" "text-[10px]" "px-2" "py-1" "bg-slate-100"
                            "first:rounded-md-tl" "last:rounded-md-tr" "text-left" "whitespace-nowrap"]
                           (:extra-classes opts))}]
          content)))

(defn table-body [& content]
  (into [:tbody] content))

(defn table-header [& content]
  (into [:thead] content))

(defn table+
  "Static hiccup-based tables for quickly showing arbitrary table content. Allows quick adding of custom
   cell content without viewers, etc."
  [& content]
  (let [[props content] (if (map? (first content))
                          [(first content) (rest content)]
                          [nil content])]
    [:div.bg-white.rounded-lg.border.border-slate-300.shadow-sm.font-sans.text-sm.not-prose.overflow-x-auto
     (into [:table.w-full props] content)]))

(defn modal-close [& [attrs]]
  [:div.sticky.right-0.top-0 {:style {:z-index (dec (z-index :modal-close))}}
   [:button
    (merge {:style {:z-index (z-index :modal-close)}
            :class "absolute top-2 right-2 p-2 text-gray-300 bg-white rounded hover:text-gray-500 cursor-pointer"} (with-events attrs))
    [icon {:size 24} :x]]])

(defn modal [opts & content]
  (let [{:keys [on-close close-attrs attrs] :as opts} (with-events opts)]
    [alert {:open? true
            :on-close on-close}
     [alert-content (merge {:full? true} attrs)
      (into [:<> [modal-close (merge {:on-click on-close} close-attrs)]]
            content)]]))

(defn to-iframe [html]
  [:iframe
   {:width "100%"
    :height 800
    :ref (fn [el]
           (when el
             (.. el -contentWindow -document (write html))))}])

(defn email [html]
  [:div.bg-gray-50
   [:div.p-6.border-b.flex.items-center
    [to-iframe html]]])

(defn flash-notice [{:keys [type message on-close open?]}]
  (r/with-let [!hidden? (r/atom false)]
    (let [type (or type :info)]
      (if @!hidden?
        [:<>]
        [:div.p-4
         {:class (case type
                   :attention "bg-yellow-50"
                   :error "bg-red-50"
                   :success "bg-green-50"
                   :info "bg-blue-50")}
         [:div.flex.justify-between
          [:div.flex
           [:div.flex-shrink-0
            (case type
              :attention [:svg.h-5.w-5.text-yellow-400 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20" :fill "currentColor" :aria-hidden "true"}
                          [:path {:fill-rule "evenodd" :d "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" :clip-rule "evenodd"}]]
              :error [:svg.h-5.w-5.text-red-500 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20" :fill "currentColor" :aria-hidden "true"}
                      [:path {:fill-rule "evenodd" :d "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" :clip-rule "evenodd"}]]
              :success [:svg.h-5.w-5.text-green-400 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20" :fill "currentColor" :aria-hidden "true"}
                        [:path {:fill-rule "evenodd" :d "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" :clip-rule "evenodd"}]]
              :info [:svg.h-5.w-5.text-blue-400 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20" :fill "currentColor" :aria-hidden "true"}
                     [:path {:fill-rule "evenodd" :d "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" :clip-rule "evenodd"}]])]
           [:div.ml-3.text-sm
            {:class (case type
                      :attention "text-yellow-700"
                      :error "text-red-700"
                      :success "text-green-700"
                      :info "text-blue-700")}
            message]]
          (when (or on-close
                    (nil? open?))
            [:div.ml-auto.pl-3
             [:div.-mx-1.5.-my-1.5
              [:button.inline-flex.rounded-md.p-1.cursor-pointer
               {:class "hover:bg-black/5"
                :on-click #(do
                             (when on-close (on-close))
                             (when-not open? (reset! !hidden? true)))}
               [:span.sr-only "Dismiss"]
               [:svg.h-5.w-5 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20" :fill "currentColor" :aria-hidden "true"
                              :class (case type
                                       :attention "text-yellow-500"
                                       :error "text-red-500"
                                       :success "text-green-500"
                                       :info "text-blue-500")}
                [:path {:fill-rule "evenodd" :d "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" :clip-rule "evenodd"}]]]]])]]))))

(defscene show-flash-notice
  [:<>
   [:div.mb-8
    [flash-notice {:type :info :message [:span "A new software update is available. "
                                         [:a.underline {:href "#"} "See what’s new in version 2.0.4 →"]]}]]
   [:div.mb-8
    [flash-notice {:type :attention :message [:span "You have no credits left. "
                                              [:a.underline {:href "#"} "Upgrade your account to add more credits."]]}]]
   [:div.mb-8
    [flash-notice {:type :success :message "Your password has been reset."}]]
   [:div.mb-8
    [flash-notice {:type :error :message [:div
                                          [:div.font-bold "There were 2 errors with your submission"]
                                          [:div.mt-2
                                           [:ul.list-disc.pl-5.space-y-1
                                            [:li "Your password must be at least 8 characters"]
                                            [:li "Your password must include at least one pro wrestling finishing move"]]]]}]]])

(defn spinner [{:keys [size class]}]
  (let [size (or size 8)]
    (into
     [:svg.animate-spin.text-indigo-600
      {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :width size :height size :class (or class "block")}
      [:circle.opacity-25 {:cx "12" :cy "12" :r "10" :stroke "currentColor" :stroke-width "4"}]
      [:path.opacity-75 {:fill "currentColor" :d "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"}]])))

(defn optionality-badge
  ([] (optionality-badge :optionality/can))
  ([optionality]
   [badge {:variant (case optionality
                      :optionality/must :red
                      :optionality/should :orange
                      :optionality/can :blue)
           :size 1
           :class "uppercase"}
    (case optionality
      :optionality/must (tr "Must")
      :optionality/should (tr "Should")
      :optionality/can (tr "Can"))]))

(defscene badges
  [:div.p-6
   [:div.uppercase.tracking-wide.text-xs.mb-4.font-medium
    "Badges"]
   [:div.flex.gap-2.p-3.flex-wrap.flex-col.text-xs
    [:div "primary " [badge {:variant :primary :size 1} "Paket LEAN"]]
    [:div "secondary " [badge {:variant :secondary :size 1} "Paket LEAN"]]
    [:div "green " [badge {:variant :green :size 1} "Paket LEAN"]]
    [:div "red " [badge {:variant :red :size 1} "MUSS"]]
    [:div "orange " [badge {:variant :orange :size 1} "SOLL"]]
    [:div "blue " [badge {:variant :blue :size 1} "KANN"]]
    [:div "gray " [badge {:variant :gray :size 1} "1"]]
    [:div "gray " [badge {:variant :gray :size 1} "5"]]
    [:div "gray " [badge {:variant :gray :size 1} "23"]]
    [:div "yellow " [badge {:variant :yellow :size 1} "Paket LEAN"]]
    [:div "white-green " [badge {:variant :white-green :size 1} "Paket LEAN"]]
    [:div "white-blue " [badge {:variant :white-blue :size 1} "Paket LEAN"]]
    [:div "white-gray " [badge {:variant :white-gray :size 1} "Paket LEAN"]]]

   [:div.flex.gap-2.bg-slate-100.p-3.flex-wrap ":size 1"
    [:div [badge {:variant :primary :size 1} "Paket LEAN"]]
    [:div [badge {:variant :secondary :size 1} "Paket LEAN"]]
    [:div [badge {:variant :green :size 1} "Paket LEAN"]]
    [:div [badge {:variant :red :size 1} "MUSS"]]
    [:div [badge {:variant :orange :size 1} "SOLL"]]
    [:div [badge {:variant :blue :size 1} "KANN"]]
    [:div [badge {:variant :gray :size 1} "1"]]
    [:div [badge {:variant :gray :size 1} "5"]]
    [:div [badge {:variant :gray :size 1} "23"]]
    [:div [badge {:variant :yellow :size 1} "Paket LEAN"]]
    [:div [badge {:variant :white-green :size 1} "Paket LEAN"]]
    [:div [badge {:variant :white-blue :size 1} "Paket LEAN"]]
    [:div [badge {:variant :white-gray :size 1} "Paket LEAN"]]]

   [:div.flex.gap-2.bg-slate-100.p-3.flex-wrap ":size 2"
    [:div [badge {:variant :primary :size 2} "Paket LEAN"]]
    [:div [badge {:variant :secondary :size 2} "Paket LEAN"]]
    [:div [badge {:variant :green :size 2} "Paket LEAN"]]
    [:div [badge {:variant :red :size 2} "MUSS"]]
    [:div [badge {:variant :orange :size 2} "SOLL"]]
    [:div [badge {:variant :blue :size 2} "KANN"]]
    [:div [badge {:variant :gray :size 2} "1"]]
    [:div [badge {:variant :gray :size 2} "5"]]
    [:div [badge {:variant :gray :size 2} "23"]]
    [:div [badge {:variant :yellow :size 2} "Paket LEAN"]]
    [:div [badge {:variant :white-green :size 2} "Paket LEAN"]]
    [:div [badge {:variant :white-blue :size 2} "Paket LEAN"]]
    [:div [badge {:variant :white-gray :size 2} "Paket LEAN"]]]

   [:div.flex.gap-2.bg-slate-100.p-3.flex-wrap
    [:div [badge {:variant :primary :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :secondary :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :green :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :red :size 2 :on-close identity} "MUSS"]]
    [:div [badge {:variant :orange :size 2 :on-close identity} "SOLL"]]
    [:div [badge {:variant :blue :size 2 :on-close identity} "KANN"]]
    [:div [badge {:variant :gray :size 2} "1"]]
    [:div [badge {:variant :gray :size 2} "5"]]
    [:div [badge {:variant :gray :size 2} "23"]]
    [:div [badge {:variant :gray :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :yellow :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :white-green :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :white-blue :size 2 :on-close identity} "Paket LEAN"]]
    [:div [badge {:variant :white-gray :size 2 :on-close identity} "Paket LEAN"]]]
   [:div.flex.gap-2.bg-slate-100.p-3
    [:p "x" [badge {:variant :green :size 2} "Paket LEAN"] "y"]]])

(defn error-view [{:as e :keys [error error-message stack]}]
  (r/with-let [!stack-expanded (r/atom false)]
    (when e
      [:div.bg-red-100.not-prose.text-red-700.px-4.lg:px-8.py-4.flex
       [:div.mr-4
        [:svg {:width "36", :height "36", :viewBox "0 0 36 36", :fill "none", :xmlns "http://www.w3.org/2000/svg"}
         [:path {:d "M18 13.4999V19.1249M4.04548 24.1889C2.74648 26.4389 4.37098 29.2499 6.96748 29.2499H29.0325C31.6275 29.2499 33.252 26.4389 31.9545 24.1889L20.9235 5.06689C19.6245 2.81689 16.3755 2.81689 15.0765 5.06689L4.04548 24.1889ZM18 23.6249H18.0105V23.6369H18V23.6249Z", :stroke "currentColor", :stroke-width "2.5", :stroke-linecap "round", :stroke-linejoin "round"}]]]
       [:div {:class "mt-[2px]"}
        [:div.lg:text-lg.font-bold error-message]
        [:div.mt-2.text-sm.lg:text-base
         (tr "If this error keeps happening to you") " "
         [:a.font-bold.underline.hover:opacity-70.transition-all {:href "mailto:help@ductile.de"} (tr "please get in touch")] "."]
        (when-let [error-data (not-empty (dissoc e :error :error-message :stack))]
          [:div.font-mono.text-xs.overflow-x-auto.mt-2
           [clerk.render/inspect error-data]])
        [:div.mt-2.font-mono {:class "text-[11px]"} (tr "Error code") ": " (str error)]
        (when-let [stack (and stack (try
                                      (->> stack
                                           str/split-lines
                                           (drop 1)
                                           (mapv str/trim))
                                      (catch js/Error _ nil)))]
          [:pre.text-red-600.dark:text-red-300.w-full.overflow-auto.mt-2 {:class "text-[11px] max-h-[155px]"}
           [:span.underline.cursor-pointer {:on-click #(swap! !stack-expanded not)}
            (if @!stack-expanded "Hide" "Show")
            " Stacktrace (" (count stack) " lines)\n"]
           (when @!stack-expanded
             (str/join "\n" stack))])]])))

(defn expected-error-view [{:as e :keys [error error-message stack]}]
  (r/with-let [!details-visible? (r/atom false)]
    [:div.bg-red-100.not-prose.text-red-700.px-4.lg:px-8.py-4.flex.expected-error
     {:ref #(when % (scroll-into-view-with-margin! % {:smooth? true}))}
     [:div.mr-4
      [:svg {:width "36", :height "36", :viewBox "0 0 36 36", :fill "none", :xmlns "http://www.w3.org/2000/svg"}
       [:path {:d "M18 13.4999V19.1249M4.04548 24.1889C2.74648 26.4389 4.37098 29.2499 6.96748 29.2499H29.0325C31.6275 29.2499 33.252 26.4389 31.9545 24.1889L20.9235 5.06689C19.6245 2.81689 16.3755 2.81689 15.0765 5.06689L4.04548 24.1889ZM18 23.6249H18.0105V23.6369H18V23.6249Z", :stroke "currentColor", :stroke-width "2.5", :stroke-linecap "round", :stroke-linejoin "round"}]]]
     [:div {:class "mt-[2px]"}
      [:div.lg:text-lg.font-bold error-message]
      [:div.text-xs.cursor-pointer {:on-click #(swap! !details-visible? not)}
       (if @!details-visible? (tr "Hide details") (tr "Show details"))]
      (when @!details-visible?
        [:<>
         (when-let [error-data (not-empty (dissoc e :error :error-message))]
           [:div.font-mono.text-xs.overflow-x-auto.mt-2
            [clerk.render/inspect error-data]])
         [:div.mt-2.font-mono {:class "text-[11px]"} (tr "Error code") ": " (str error)]])]]))

(defn loading-promise-view [view-fn promise]
  (let [result (hooks/use-promise promise)]
    (cond (not result)
          [:div.bg-white.md:bg-gray-200
           [loading-spinner]]

          (:error result)
          [error-view result]

          result
          [view-fn result])))

(defn loading-promise-view-when-authorized [view-fn promise]
  (let [result (hooks/use-promise promise)]
    (cond (not result)
          [:div.bg-white.md:bg-gray-200
           [loading-spinner]]

          (= :ductile.lambda/not-authorized (:error result))
          nil

          (:error result)
          [error-view result]

          result
          [view-fn result])))

(def unassigned
  [:span.text-slate10 (tr "Unassigned")])



(def carrier-attrs
  #{:transport/carrier :transport/license-plate})

(defn loading-indicator-with-label [label]
  [:div.text-xs.flex.items-center.gap-2.text-slate10
   [:svg {:class "animate-spin h-5 w-5 text-indigo-700", :xmlns "http://www.w3.org/2000/svg", :fill "none", :viewBox "0 0 24 24"}
    [:circle {:class "opacity-25", :cx "12", :cy "12", :r "10", :stroke "currentColor", :stroke-width "4"}]
    [:path {:class "opacity-75", :fill "currentColor", :d "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"}]]
   label])

(defn transport-label [transport]
  (str (:load/id transport)
       (when (:transport/load-id transport)
         (str " / " (:transport/load-id transport)))
       (when (:transport/name transport)
         (str " (" (:transport/name transport) ")"))
       (when-let [truck-number (:truck/number transport)]
         (str " "
              (if (= "SA" truck-number)
                (tr "self-pickup")
                (str (tr "Truck") " " truck-number))))))

(defn stepper [{:keys [value on-change min max data-testid]}]
  (let [value (or value 0)
        can-inc? (< value max)
        can-dec? (< min value)]
    [:div.flex.shrink-0
     [:button.flex.items-center.rounded-l-full.text-white.px-2.cursor-pointer.bg-indigo-600.enabled:hover:bg-indigo-700.enabled:active:bg-indigo-600.disabled:bg-indigo-300
      {:type :button ; otherwise submits parent form
       :class "py-0.5"
       :disabled (not can-dec?)
       :on-click #(on-change (dec value))
       :data-testid (str data-testid "-dec")}
      [icon {:size 14} :minus]]
     [:div.font-semibold.tabular-nums.flex.items-center.justify-center.bg-white.text-indigo-600.px-3.border-t-2.border-b-2
      {:class (if (and can-inc? can-dec?) "border-indigo-600" "border-indigo-300")}
      (str value)]
     [:button.flex.items-center.rounded-r-full.text-white.px-2.enabled:cursor-pointer.bg-indigo-600.enabled:hover:bg-indigo-700.enabled:active:bg-indigo-600.disabled:bg-indigo-300
      {:type :button ; otherwise submits parent form
       :class "py-0.5"
       :disabled (not can-inc?)
       :on-click #(on-change (inc value))
       :data-testid (str data-testid "-inc")}
      [icon {:size 14} :plus]]]))

(defn labels-viewer [{:as opts :keys [labels]}]
  (->> labels
       (filter identity)
       (into [:div.inline-flex.items-center.gap-1]
             (map (fn [label]
                    (let [v (if (str/blank? (tr label)) (name label) (tr label))]
                      [ductile.ui.components/badge (merge {:variant :white-green :size 1} opts) v]))))))

(defn progress-viewer [{:keys [done total]}]
  (when-let [percentage (and (pos? total)
                             (str (int (* 100 (/ done total)))
                                  "%"))]
    [:div.w-full.bg-mauve5.rounded-full.relative.h-3.shadow-inner
     [:div.bg-indigo-700.text-white.leading-none.rounded-full.h-3
      {:class "py-[2px] text-[11px]" :style {:width percentage}}]
     [:div.absolute.left-0.top-0.h-3.leading-none.flex.items-center.text-white
      {:class ["text-[10px]"]
       :style {:left (str "max(5px, calc(" percentage " - 35px))") :width 35}}
      percentage]]))

(defn time-picker [opts]
  (let [{:keys [^js value on-change class data-testid]} (with-events opts)
        value (or value (now))
        time-value (str (.padStart (str (.getHours value)) 2 "0") ":"
                        (.padStart (str (.getMinutes value)) 2 "0"))
        on-change-time (fn [e]
                         (let [[hh mm] (str/split (.. e -target -value) #":")]
                           (on-change
                            (doto (js/Date. (.getTime value))
                              (.setHours (parse-long hh))
                              (.setMinutes (parse-long mm))
                              (.setSeconds 0)
                              (.setMilliseconds 0)))))]
    [input
     {:type :time
      :value time-value
      :on-change on-change-time
      :width :sm
      :data-testid (when data-testid (str data-testid "-time"))}]))

(defn format-weekday-short [^js date]
  (.format (js/Intl.DateTimeFormat
            (locale/get-current-iso-locale)
            #js {:weekday "short"}) date))

(defn date-picker [opts]
  (let [{:keys [^js value on-change date days-before days-after class data-testid]} (with-events opts)
        !ref (hooks/use-ref)
        date (or date (now))
        on-change-date (fn [e]
                         (on-change (js/Date. (js/Date.parse (.. e -target -value)))))]
    [input
     (cond-> {:type :date
              :ref !ref
              :on-change on-change-date
              :value (if value (format-date value) "")
              :data-testid (when data-testid (str data-testid "-date"))
              :width :md}
       days-before (assoc :min (add-days date (- days-before)))
       days-after (assoc :max (add-days date days-after)))]))

(defn datetime-picker [opts]
  (let [opts (update opts :value #(if % (js/Date. (.getTime %)) (js/Date.)))]
    [:div.flex.items-center.gap-2
     [date-picker opts]
     [time-picker opts]]))



(defscene show-inputs
  [:div.text-gray-500.flex.flex-col.gap-6.pl-6
   [:div
    [:div.uppercase.tracking-wide.text-xs.mb-4.font-medium
     "Buttons"]

    [:div.flex
     [:div.m-2 [button {:variant :secondary :size 1} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 2} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 4} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :secondary :size 1 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 2 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 4 :disabled? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :primary :size 1} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 2} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 4} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :primary :size 1 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 2 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 4 :disabled? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :white :size 1} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 2} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 4} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :white :size 1 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 2 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 4 :disabled? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :destructive :size 1} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 2} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 4} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :destructive :size 1 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 2 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 4 :disabled? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :destructive-white :size 1} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 2} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 4} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :destructive-white :size 1 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 2 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 4 :disabled? true} "Auswahl speichern"]]]

    [:div
     [:div.m-2 [button {:variant :secondary :size 2 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 2 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 2 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 2 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 2 :loading? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :secondary :size 1 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 2 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 3 :loading? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 4 :loading? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :secondary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 3} "Auswahl speichern"]]]

    [:div.flex.bg-slate-100.py-2
     [:div.m-2 [button {:variant :secondary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 3} "Auswahl speichern"]]]

    [:div.flex.bg-slate-100.py-4
     [:div.m-2 [button {:variant :secondary :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :white :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :destructive-white :size 3 :disabled? true} "Auswahl speichern"]]]

    [:div.flex
     [:div.m-2 [button {:variant :secondary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 3} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :secondary :size 3 :disabled? true} "Auswahl speichern"]]
     [:div.m-2 [button {:variant :primary :size 3} "Auswahl speichern"]]]]

   [:div
    [:div.uppercase.tracking-wide.text-xs.mb-4.font-medium
     "Datumsauswahl"]
    [:div.flex.items-center.m-2
     [native-date-picker nil #inst "2021-10-08T12:16:41.761088" identity]]]

   [:div
    [:div.uppercase.tracking-wide.text-xs.mb-4.font-medium
     "Eingabefelder"]
    [:div.mb-4.m-2
     [input
      {:placeholder "Vorname"
       :style {:width 300}}]]]])

(defn service-notifications [service]
  (into [:<>]
        (map (fn [notification]
               (let [slug (str "/compound/"
                               @deframe/!current-compound
                               "/"
                               (get {:email.bmw-unfall/entry "entry-protocol"
                                     :email.arval/entry "entry-protocol"}
                                    (:notification/kind notification))
                               "/"
                               (:ductile/id service))]
                 [:div.flex.items-center.gap-2
                  [icon-socket [icon {:size 16} :mail]]
                  [:span
                   (if (= :unprocessed (:notification/status notification))
                     [:span [link {:href slug
                                   :target "_blank"} (tr (:notification/kind notification))]
                      (str " " (tr "has not been created yet") ".")]
                     [:<>
                      [link
                       {:href (str "/full-email/" (:ductile/id notification))
                        :target "_blank"}
                       (tr (:notification/kind notification))]
                      (str " "
                           (if-let [sent-at (:notification/sent-at notification)]
                             (str (tr "was sent at") " " (time-format/->str {:style :short} sent-at) ".")
                             (str (tr "has been created") ".")))])]])))
        (:ductile/notifications service)))

(defn toast! [{:as opts :keys [title description variant]}]
  (deframe/tx!
   [[:op/assoc :toast (merge {:variant :success} opts)]]))

(defn keys-hint [& ks]
  (let [opts (first ks)
        opts? (map? opts)]
    (into [:div.inline-flex {:class "gap-[2px]"}]
          (map (fn [k]
                 [:kbd.font-inter
                  {:class (str "text-[10px] px-[4px] rounded-[3px] border shadow-sm font-normal flex items-center h-[18px] leading-none "
                               (if (and opts? (:dark? opts))
                                 "bg-black bg-opacity-30 text-white border-black border-opacity-40"
                                 "bg-white border-slate-300 text-slate-800 "))}
                  k]))
          (if opts? (rest ks) ks))))

(defn page-title [title buttons]
  [:div.flex.justify-between [:h1.text-xl.mb-3 title] buttons])

(defn fallback-render-value [v _row _attr]
  (cond
    (boolean? v) (if v (tr "Yes") (tr "No"))
    (keyword? v) (tr v)
    (number? v) [:span.tabular-nums (locale/format-number v)]
    (inst? v) [:span.tabular-nums (time-format/->str {:format :date} v)]
    (and (vector? v) (or (fn? (first v)) (keyword? (first v)))) v
    (nil? v) nil
    :else (str v)))

(defn safe-render-value [{:as header-props :keys [render]} row attr]
  (cond-> (get row attr)
    render (render row attr)
    true (fallback-render-value row attr)))

(defn -table-structure-mobile [{:keys [props header-attrs header-props render-cell]} data]
  (into [:<>]
        (map (fn [{:as row :table/keys [colspan-full collapsed?]}]
               (cond
                 collapsed? nil
                 colspan-full colspan-full
                 :else [:table (props :table row)
                        (into [:tbody (props :tbody row)]
                              (map (fn [attr]
                                     [:tr (props :tr attr row)
                                      (let [label (get-in header-props [attr :label] attr)]
                                        [:th (props :th attr row) (if (or (string? label) (keyword? label))
                                                                    (tr label)
                                                                    label)])
                                      [:td
                                       (props :td (get row attr) row attr)
                                       (render-cell row attr)]]))
                              header-attrs)])))
        data))

(defn -table-structure-desktop [{:keys [props header-attrs header-props render-cell]} data]
  [:table
   (props :table data)
   [:thead
    (props :thead header-attrs)
    (into [:tr (props :thead-tr header-attrs)]
          (map (fn [attr]
                 (let [label (get-in header-props [attr :label] attr)]
                   [:th (props :th attr)
                    (if (or (string? label) (keyword? label))
                      (tr label)
                      label)])))
          header-attrs)]
   (into [:tbody (props :tbody data)]
         (map (fn [{:as row :table/keys [colspan-full collapsed?]}]
                (if colspan-full
                  [:tr
                   [:td {:col-span (count header-attrs)}
                    colspan-full]]

                  (into
                   [:tr (cond-> (props :tr row)
                          collapsed? (assoc-in [:style :visibility] :collapse))]
                   (map (fn [attr] [:td
                                    (props :td (get row attr) row attr)
                                    (render-cell row attr)]))
                   header-attrs))))
         data)])

(defn simple-table [{:as opts :keys [schema mobile? _table _thead _tr _th _tbody _td]} data]
  (let [props (fn [element & args] (let [prop (get opts element)]
                                     (cond-> prop (fn? prop) (apply args))))
        header-attrs (map (fn [attr] (if (vector? attr) (first attr) attr)) schema)
        header-props (into {} (comp (map (fn [attr]
                                           (when (vector? attr)
                                             [(first attr) (second attr)])))
                                    (remove nil?)) schema)
        render-cell (fn [row attr] (safe-render-value (get header-props attr) row attr))
        derive-all (->> header-props
                        (map (fn [[attr {:keys [derive]}]] (when derive #(assoc % attr (derive %)))))
                        (remove nil?)
                        (apply comp))
        opts' (assoc opts :props props :header-attrs header-attrs :header-props header-props :render-cell render-cell)
        data (map derive-all data)]
    [(if mobile? -table-structure-mobile -table-structure-desktop) opts' data]))

(defn with-hardware-scanner [scanner-view scan-tracker & content]
  (r/with-let [_ (deframe/set-scan-tracker! scan-tracker)]
    [:<>
     (into [:div]
           content)
     (when scanner-view [scanner-view])]
    (finally
      (deframe/dispose-scan-tracker! scan-tracker))))
